import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent, CartUpdateEvent } from '@theme/events';

/**
 * Product Calculator Component
 * 
 * Calculates required packs and pricing based on area input.
 * Uses variant pack_coverage metafields and pack_type to determine optimal pack combination.
 */
/**
 * @typedef {'full'|'half'|'quarter'} PackType
 * @typedef {{id:number, packType:PackType, price:number, coverage:number}} PackVariant
 * @typedef {{full:number, half:number, quarter:number}} PackBreakdown
 * @typedef {{full:number, half:number, quarter:number, total:number}} PackCosts
 * @typedef {{postcode:string, zone:string, type:string, estimated:string, date:string, cost:number}} DeliveryState
 * @typedef {{name:string, price:number}} ShippingRate
 */

export default class ProductCalculator extends Component {
  /** @type {any[]} */
  variants = [];
  /** @type {Record<PackType, PackVariant>} */
  variantMap = /** @type {Record<PackType, PackVariant>} */ ({});
  defaultWastage = 10;
  pricePerM2 = 0;
  currency = 'GBP';
  
  // Delivery fee product variant ID (set via data attribute if available)
  deliveryFeeVariantId = null;
  
  /** @type {any[]} */
  deliveryVariants = [];
  /** @type {Record<string, Record<string, any>>} */
  deliveryVariantMap = /** @type {Record<string, Record<string, any>>} */ ({});

  // Stored calculated values
  calculatedArea = 0;
  /** @type {PackBreakdown} */
  calculatedPacks = { full: 0, half: 0, quarter: 0 };
  calculatedCost = 0; // Total pack cost in pence
  calculatedM2Cost = 0; // Total m² cost in pounds

  /** @type {DeliveryState} */
  deliveryState = { postcode: '', zone: '', type: 'standard', estimated: '', date: '', cost: 0 };
  
  /** @type {boolean} */
  userManuallySelectedDeliveryType = false;
  
  /** @type {ReturnType<typeof setTimeout>|null} */
  deliveryTypeFeedbackTimeout = null;

  /** @type {ReturnType<typeof setTimeout>|null} */
  postcodeCheckTimeout = null;
  
  /** @type {Map<string, string>} Map of blocked dates (YYYY-MM-DD) to holiday names */
  blockedHolidays = new Map();

  /** When false, Express option is hidden and pricing uses Standard only (weekday) */
  expressAvailable = true;

  connectedCallback() {
    super.connectedCallback();
    
    // Parse variant data from JSON
    const variantsJson = this.dataset.variants || '[]';
    
    // Handle HTML entity encoding
    let decodedJson = variantsJson;
    if (decodedJson.includes('&quot;')) {
      decodedJson = decodedJson.replace(/&quot;/g, '"');
    }
    if (decodedJson.includes('&#39;')) {
      decodedJson = decodedJson.replace(/&#39;/g, "'");
    }
    
    try {
      this.variants = JSON.parse(decodedJson);
    } catch (e) {
      console.error('Calculator: Failed to parse variants', e);
      this.variants = [];
    }
    
    // Parse delivery variant data from JSON
    const deliveryVariantsJson = this.dataset.deliveryVariants || '[]';
    let decodedDeliveryJson = deliveryVariantsJson;
    if (decodedDeliveryJson.includes('&quot;')) {
      decodedDeliveryJson = decodedDeliveryJson.replace(/&quot;/g, '"');
    }
    if (decodedDeliveryJson.includes('&#39;')) {
      decodedDeliveryJson = decodedDeliveryJson.replace(/&#39;/g, "'");
    }
    
    try {
      this.deliveryVariants = JSON.parse(decodedDeliveryJson);
      this.deliveryVariantMap = this.organizeDeliveryVariants(this.deliveryVariants);
    } catch (e) {
      console.error('Calculator: Failed to parse delivery variants', e);
      this.deliveryVariants = [];
      this.deliveryVariantMap = {};
    }
    
    // Get configuration
    const defaultWastageStr = this.dataset.defaultWastage || '10';
    const pricePerM2Str = this.dataset.pricePerM2 || '0';
    const currencyStr = this.dataset.currency || 'GBP';
    
    this.defaultWastage = parseFloat(defaultWastageStr) || 10;
    this.pricePerM2 = parseFloat(pricePerM2Str) || 0; // Price per m² in pounds
    this.currency = currencyStr;

    this.expressAvailable = this.dataset.expressAvailable !== 'false';
    
    // Get sample variant ID if configured
    const sampleVariantIdStr = this.dataset.sampleVariantId || '';
    this.sampleVariantId = sampleVariantIdStr ? parseInt(sampleVariantIdStr, 10) : null;
    
    // Parse blocked delivery holidays from store metafield
    this.parseBlockedHolidays();
    
    // Organize variants by pack type
    this.variantMap = this.organizeVariants(this.variants);
    
    // Wait for refs to be available
    requestAnimationFrame(() => {
      // Set default wastage in input
      const wastageInput = this.refs.wastageInput;
      if (wastageInput && !Array.isArray(wastageInput) && wastageInput instanceof HTMLInputElement) {
        wastageInput.value = String(this.defaultWastage);
      }

      // Initialize results display
      this.showLoadingState();
      
      // Initialize price per m² unit display
      this.updateM2PriceUnitDisplay();

      // Add direct event listeners
      const lengthInput = this.refs.lengthInput;
      const widthInput = this.refs.widthInput;
      const wastageInputEl = this.refs.wastageInput;
      const deliveryCta = this.refs.deliveryCta;
      const deliveryCheckButton = this.refs.deliveryCheckButton;
      const deliveryTypeSelect = this.refs.deliveryTypeSelect;
      const addToCartButton = this.refs.addToCartButton;
      
      if (lengthInput && !Array.isArray(lengthInput) && lengthInput instanceof HTMLInputElement) {
        lengthInput.addEventListener('input', () => this.calculate());
      }
      if (widthInput && !Array.isArray(widthInput) && widthInput instanceof HTMLInputElement) {
        widthInput.addEventListener('input', () => this.calculate());
      }
      if (wastageInputEl && !Array.isArray(wastageInputEl) && wastageInputEl instanceof HTMLInputElement) {
        wastageInputEl.addEventListener('input', () => this.calculate());
      }

      if (deliveryCta && deliveryCta instanceof HTMLElement) {
        deliveryCta.addEventListener('click', (event) => {
          event.preventDefault();
          this.handleDeliveryStart();
        });
      }

      if (deliveryTypeSelect && deliveryTypeSelect instanceof HTMLSelectElement) {
        // Make the select field read-only (disabled but visible)
        deliveryTypeSelect.disabled = true;
        deliveryTypeSelect.style.cursor = 'not-allowed';
        deliveryTypeSelect.style.opacity = '0.7';
        deliveryTypeSelect.style.pointerEvents = 'none'; // Prevent any interaction
        deliveryTypeSelect.setAttribute('aria-readonly', 'true');
        deliveryTypeSelect.setAttribute('tabindex', '-1'); // Remove from tab order
        deliveryTypeSelect.title = 'Delivery method is automatically selected based on your delivery date';
        
        // Prevent any click/change events
        deliveryTypeSelect.addEventListener('mousedown', (e) => {
          e.preventDefault();
          e.stopPropagation();
        });
        deliveryTypeSelect.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
        });
        deliveryTypeSelect.addEventListener('change', (e) => {
          e.preventDefault();
          e.stopPropagation();
        });
      }

      if (addToCartButton && addToCartButton instanceof HTMLElement) {
        addToCartButton.addEventListener('click', (event) => {
          event.preventDefault();
          this.handleAddToCart();
        });
      }

      const deliveryDateInput = this.refs.deliveryDateInput;
      if (deliveryDateInput && deliveryDateInput instanceof HTMLInputElement) {
        // Earliest selectable date: express window when Express is offered, else standard lead time only
        deliveryDateInput.setAttribute('min', this.getEffectiveMinimumDeliveryDateString());
        
        // Validation is handled in validateDeliveryDate method
        
        // Set default value to first available standard delivery date (free delivery)
        if (!deliveryDateInput.value) {
          const defaultDate = this.calculateFirstAvailableDeliveryDate('standard');
          deliveryDateInput.value = defaultDate;
          // Update delivery type options based on default date
          this.updateDeliveryTypeOptions(defaultDate);
          // Sync type with default date
          this.syncTypeWithDate(defaultDate);
        } else {
          // Update delivery type options based on existing date value
          this.updateDeliveryTypeOptions(deliveryDateInput.value);
          // Sync type with existing date
          this.syncTypeWithDate(deliveryDateInput.value);
        }
        
        // Make the entire field clickable (not just icon)
        deliveryDateInput.style.cursor = 'pointer';
        
        // Add click listener to open date picker (with fallback for older browsers)
        deliveryDateInput.addEventListener('click', (e) => {
          // If showPicker is supported, use it; otherwise let default behavior work
          if (typeof deliveryDateInput.showPicker === 'function') {
            e.preventDefault();
            deliveryDateInput.showPicker();
          }
          // For browsers without showPicker, the default click behavior will focus the input
        });
        
        // Also make the label clickable
        const dateLabel = document.querySelector(`label[for="${deliveryDateInput.id}"]`);
        if (dateLabel && dateLabel instanceof HTMLElement) {
          dateLabel.style.cursor = 'pointer';
          dateLabel.addEventListener('click', () => {
            deliveryDateInput.focus();
            if (typeof deliveryDateInput.showPicker === 'function') {
              deliveryDateInput.showPicker();
            }
          });
        }
        
        deliveryDateInput.addEventListener('input', () => {
          // Validate date (prevent Sundays, too soon, holidays)
          if (!this.validateDeliveryDate(deliveryDateInput.value)) {
            return;
          }
          this.syncTypeWithDate(deliveryDateInput.value);
          this.updatePostcodeFieldState();
          // Clear date error: show next step (Enter postcode or recalc delivery)
          if (deliveryDateInput.value && !this.deliveryState.postcode) {
            this.setDeliveryResult('Enter postcode to check delivery.', 'info');
          } else if (this.deliveryState.zone && this.deliveryState.postcode) {
            this.handleDeliveryCheck(true);
          }
        });
        
        deliveryDateInput.addEventListener('change', () => {
          // Validate date (prevent Sundays, too soon, holidays)
          if (!this.validateDeliveryDate(deliveryDateInput.value)) {
            return;
          }
          this.syncTypeWithDate(deliveryDateInput.value);
          this.updatePostcodeFieldState();
          // Clear date error: show next step
          if (deliveryDateInput.value && !this.deliveryState.postcode) {
            this.setDeliveryResult('Enter postcode to check delivery.', 'info');
          } else if (this.deliveryState.zone && this.deliveryState.postcode) {
            this.handleDeliveryCheck(true);
          }
        });
      }

      const deliveryPostcodeInput = this.refs.deliveryPostcodeInput;
      if (deliveryPostcodeInput && deliveryPostcodeInput instanceof HTMLInputElement) {
        // Validate date is selected before allowing postcode input
        deliveryPostcodeInput.addEventListener('focus', () => {
          const deliveryDateInput = this.refs.deliveryDateInput;
          if (deliveryDateInput && deliveryDateInput instanceof HTMLInputElement && !deliveryDateInput.value) {
            this.setDeliveryResult('Please select a delivery date first.', 'error');
            deliveryDateInput.focus();
            if (typeof deliveryDateInput.showPicker === 'function') {
              deliveryDateInput.showPicker();
            }
            return;
          }
        });

        deliveryPostcodeInput.addEventListener('input', () => {
          this.handlePostcodeFieldInput(deliveryPostcodeInput);
        });

        deliveryPostcodeInput.addEventListener('paste', () => {
          requestAnimationFrame(() => {
            this.handlePostcodeFieldInput(deliveryPostcodeInput, { formatSpace: true, checkImmediately: true });
          });
        });

        deliveryPostcodeInput.addEventListener('blur', () => {
          this.normalizePostcodeInputValue(deliveryPostcodeInput, { formatSpace: true });
          const cleaned = this.sanitizePostcode(deliveryPostcodeInput.value);
          if (!cleaned) return;

          if (this.isValidUKPostcodeFormat(cleaned)) {
            this.schedulePostcodeDeliveryCheck(true);
          } else {
            this.setDeliveryResult('Invalid postcode. Please enter a valid UK postcode (e.g., SW1A 1AA).', 'error');
          }
        });

        deliveryPostcodeInput.addEventListener('change', () => {
          this.handlePostcodeFieldInput(deliveryPostcodeInput, { formatSpace: true, checkImmediately: true });
        });
      }
    });

    // Listen for variant changes
    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      closestSection.addEventListener(ThemeEvents.variantUpdate, this.handleVariantChange.bind(this));
    }
    
    // Listen for cart updates to check for Zone 4 items
    document.addEventListener('cart:update', this.handleCartUpdate.bind(this));
    document.addEventListener('cart:change', this.handleCartUpdate.bind(this));
    document.addEventListener('cart:refresh', this.handleCartUpdate.bind(this));
  }

  /**
   * Handle cart update events to check for Zone 4 items
   * @param {CustomEvent} event
   */
  handleCartUpdate(event) {
    // Fetch current cart to check for Zone 4
    fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(res => res.json())
      .then(cartJson => {
        if (cartJson) {
          this.checkCartForZone4(cartJson);
        }
      })
      .catch(err => {
        console.warn('Failed to check cart for Zone 4', err);
      });
  }

  /**
   * Derive pack type from Shopify variant data
   * Checks in order:
   * 1. variant.metafields.custom.pack_size (if present)
   * 2. variant.option1, option2, option3
   * 3. variant.title
   * Returns: 'full', 'half', 'quarter', or null
   */
  /** @param {any} variant */
  derivePackType(variant) {
    if (!variant) return null;
    
    // First check metafield if present
    const packSize = variant.metafields?.custom?.pack_size || 
                     variant.pack_size || 
                     variant.metafields?.pack_size;
    if (packSize) {
      const normalized = String(packSize).toLowerCase().trim();
      if (normalized.includes('full')) return 'full';
      if (normalized.includes('half')) return 'half';
      if (normalized.includes('quarter')) return 'quarter';
    }
    
    // Check all options and title for pack type keywords
    const searchText = [
      variant.option1 || '',
      variant.option2 || '',
      variant.option3 || '',
      variant.title || ''
    ].join(' ').toLowerCase();
    
    // Check for pack type keywords (order matters - check quarter before half to avoid false matches)
    if (searchText.includes('quarter')) {
      return 'quarter';
    } else if (searchText.includes('half')) {
      return 'half';
    } else if (searchText.includes('full')) {
      return 'full';
    }
    
    return null;
  }

  /**
   * Organize delivery variants by zone and type
   * Returns object with zone as keys, then type: { 'Zone 1': { 'standard': {...}, 'express': {...} } }
   */
  /** @param {any[]} variants */
  organizeDeliveryVariants(variants) {
    /** @type {Record<string, Record<string, any>>} */
    const map = {};
    
    if (!Array.isArray(variants) || variants.length === 0) {
      console.warn('Calculator: No delivery variants array provided');
      return map;
    }
    
    for (const variant of variants) {
      if (!variant) continue;
      
      const zone = (variant.zone || variant.option1 || '').trim();
      const type = (variant.type || variant.option2 || '').toLowerCase().trim();
      
      if (zone && type) {
        if (!map[zone]) {
          map[zone] = {};
        }
        // Normalize type names: 'saturday' -> 'weekend', 'standard' -> 'standard', 'express' -> 'express'
        let normalizedType = type;
        if (type === 'saturday' || type === 'weekend') {
          normalizedType = 'weekend';
        } else if (type === 'standard') {
          normalizedType = 'standard';
        } else if (type === 'express') {
          normalizedType = 'express';
        }
        
        map[zone][normalizedType] = {
          id: variant.id,
          zone: zone,
          type: normalizedType,
          price: variant.price || 0
        };
      }
    }
    
    return map;
  }

  /**
   * Get delivery variant ID based on zone and type
   * @param {string} zone - Zone name (e.g., 'Zone 1', 'Zone 2', 'Zone 3')
   * @param {string} type - Delivery type ('standard', 'express', 'weekend')
   * @returns {number|null} Variant ID or null if not found
   */
  getDeliveryVariantId(zone, type) {
    if (!zone || !type) return null;
    
    // Normalize type
    const normalizedType = type.toLowerCase() === 'saturday' ? 'weekend' : type.toLowerCase();
    
    const zoneMap = this.deliveryVariantMap[zone];
    if (!zoneMap) return null;
    
    const variant = zoneMap[normalizedType];
    return variant ? variant.id : null;
  }

  /**
   * Organize variants by pack type derived from Shopify variant options/title
   * Returns object with pack_type as keys: { full: {...}, half: {...}, quarter: {...} }
   */
  /** @param {any[]} variants */
  organizeVariants(variants) {
    /** @type {Record<PackType, PackVariant>} */
    const map = /** @type {Record<PackType, PackVariant>} */ ({});
    const packOrder = /** @type {PackType[]} */ (['full', 'half', 'quarter']);
    
    if (!Array.isArray(variants) || variants.length === 0) {
      console.warn('Calculator: No variants array provided');
      return map;
    }
    
    for (const variant of variants) {
      if (!variant) continue;
      
      // Derive pack type from variant option or title
      const packType = this.derivePackType(variant);
      const price = variant.price || 0;
      const coverage = variant.coverage || variant.metafields?.custom?.pack_coverage || 0;
      
      if (packType && packOrder.includes(packType) && price > 0 && coverage > 0) {
        // If we already have this pack type, keep the one with better coverage (larger)
        // This ensures we use the best variant for each pack type
        if (!map[packType] || map[packType].coverage < coverage) {
          map[packType] = {
            id: variant.id,
            packType: packType,
            price: price, // Price in pence (Shopify native)
            coverage: Number(coverage) // Coverage in m² from metafield
          };
        }
      }
    }
    
    return map;
  }

  handleInputChange() {
    this.calculate();
  }

  /** @param {CustomEvent} event */
  handleVariantChange(event) {
    // Variant changes don't affect pack data (it's product-level)
    // But we can recalculate if inputs have values
    const lengthInput = this.refs.lengthInput;
    const widthInput = this.refs.widthInput;
    if (lengthInput && widthInput && 
        !Array.isArray(lengthInput) && !Array.isArray(widthInput) &&
        lengthInput instanceof HTMLInputElement && widthInput instanceof HTMLInputElement &&
        lengthInput.value && widthInput.value) {
      this.calculate();
    }
  }

  /**
   * Main calculation function
   */
  calculate() {
    const lengthInput = this.refs.lengthInput;
    const widthInput = this.refs.widthInput;
    const wastageInput = this.refs.wastageInput;
    
    // Validate inputs exist
    if (!lengthInput || !widthInput || Array.isArray(lengthInput) || Array.isArray(widthInput)) {
      this.showLoadingState();
      this.hideSkeleton();
      this.updateActionState(false);
      return;
    }
    
    if (!(lengthInput instanceof HTMLInputElement) || !(widthInput instanceof HTMLInputElement)) {
      this.showLoadingState();
      this.hideSkeleton();
      this.updateActionState(false);
      return;
    }
    
    const length = parseFloat(lengthInput.value) || 0;
    const width = parseFloat(widthInput.value) || 0;
    let wastage = this.defaultWastage;
    if (wastageInput && !Array.isArray(wastageInput) && wastageInput instanceof HTMLInputElement) {
      wastage = parseFloat(wastageInput.value) || this.defaultWastage;
    }

    // Validate inputs - ensure reasonable values
    if (length <= 0 || width <= 0) {
      this.showLoadingState();
      this.hideSkeleton();
      this.updateActionState(false);
      return;
    }
    
    // Validate reasonable maximums (prevent unrealistic values)
    if (length > 1000 || width > 1000) {
      this.showLoadingState();
      this.hideSkeleton();
      this.updateActionState(false);
      return;
    }
    
    // Validate wastage is reasonable (0-50%)
    const wastageValue = Number(wastage) || Number(this.defaultWastage);
    const validatedWastage = (wastageValue < 0 || wastageValue > 50) ? this.defaultWastage : wastageValue;

    // Check if we have variants
    if (!this.variantMap || Object.keys(this.variantMap).length === 0) {
      console.warn('Calculator: No valid variants available', this.variantMap);
      this.showLoadingState();
      this.hideSkeleton();
      this.updateActionState(false);
      return;
    }

    // Show loading state
    this.showLoadingState();
    
    // Show skeleton loading
    requestAnimationFrame(() => {
      this.showSkeleton();
    });

    // Calculate with delay for smooth UX
    setTimeout(() => {
      // Calculate total area
      const baseArea = length * width;
      const wastageMultiplier = 1 + (validatedWastage / 100);
      const totalArea = baseArea * wastageMultiplier;

      // Calculate pack breakdown using greedy algorithm
      const packBreakdown = this.calculatePackBreakdown(totalArea);

      // Calculate costs
      const packCosts = this.calculatePackCosts(packBreakdown);
      const totalPackCost = packCosts.total; // In pence
      
      // Calculate price based on m²
      const pricePerM2Value = this.pricePerM2 || 0;
      const totalM2Cost = pricePerM2Value > 0 ? totalArea * pricePerM2Value : 0; // In pounds

      // Hide skeleton
      this.hideSkeleton();

      // Update displays
      this.updateAreaDisplay(totalArea);
      this.updatePackDisplay(packBreakdown);
      this.updateCostDisplay(packBreakdown, packCosts);
      this.updateTotalPriceDisplay(totalPackCost);
      this.updateM2PriceUnitDisplay();
      this.updateM2PriceDisplay(totalM2Cost);

      // Store calculated values
      this.calculatedArea = totalArea;
      this.calculatedPacks = packBreakdown;
      this.calculatedCost = totalPackCost;
      this.calculatedM2Cost = totalM2Cost;

      this.updateActionState(true);

      // Dispatch event
      this.dispatchEvent(new CustomEvent('calculator:updated', {
        detail: {
          area: totalArea,
          packBreakdown: packBreakdown,
          packCost: totalPackCost,
          m2Cost: totalM2Cost
        },
        bubbles: true
      }));
    }, 300);
  }

  /**
   * Calculate pack breakdown finding the optimal combination based on cost and coverage
   * 
   * Strategy: Try all possible combinations of full, half, and quarter packs
   * and select the one with the lowest total cost that meets or exceeds the required area.
   * 
   * If multiple combinations have the same cost, prefer the one with less waste
   * (closer to the required area).
   * 
   * Returns: { full: 0, half: 0, quarter: 0 }
   */
  /** @param {number} totalArea */
  calculatePackBreakdown(totalArea) {
    /** @type {PackBreakdown} */
    const breakdown = { full: 0, half: 0, quarter: 0 };
    const area = Number(totalArea) || 0;
    
    if (area <= 0) {
      return breakdown;
    }
    
    // Get available pack types
    const fullVariant = this.variantMap?.full;
    const halfVariant = this.variantMap?.half;
    const quarterVariant = this.variantMap?.quarter;
    
    // If no variants available, return empty breakdown
    if (!fullVariant && !halfVariant && !quarterVariant) {
      return breakdown;
    }
    
    // Calculate maximum quantities needed for each pack type
    // This sets a reasonable limit to avoid infinite loops
    const maxFull = fullVariant && fullVariant.coverage > 0 
      ? Math.ceil(area / fullVariant.coverage) + 2 // Add buffer for combinations
      : 0;
    const maxHalf = halfVariant && halfVariant.coverage > 0
      ? Math.ceil(area / halfVariant.coverage) + 2
      : 0;
    const maxQuarter = quarterVariant && quarterVariant.coverage > 0
      ? Math.ceil(area / quarterVariant.coverage) + 2
      : 0;
    
    // Cap maximums to prevent excessive computation (e.g., max 50 of any type)
    const MAX_ITERATIONS = 50;
    const maxFullCapped = Math.min(maxFull, MAX_ITERATIONS);
    const maxHalfCapped = Math.min(maxHalf, MAX_ITERATIONS);
    const maxQuarterCapped = Math.min(maxQuarter, MAX_ITERATIONS);
    
    /** @type {Array<{breakdown: PackBreakdown, coverage: number, cost: number, waste: number}>} */
    const validCombinations = [];
    
    // Try all possible combinations
    for (let full = 0; full <= maxFullCapped; full++) {
      for (let half = 0; half <= maxHalfCapped; half++) {
        for (let quarter = 0; quarter <= maxQuarterCapped; quarter++) {
          // Skip if all are zero
          if (full === 0 && half === 0 && quarter === 0) continue;
          
          // Calculate total coverage for this combination
          let totalCoverage = 0;
          let totalCost = 0;
          
          if (full > 0 && fullVariant && fullVariant.coverage > 0) {
            totalCoverage += full * fullVariant.coverage;
            totalCost += full * fullVariant.price;
          }
          if (half > 0 && halfVariant && halfVariant.coverage > 0) {
            totalCoverage += half * halfVariant.coverage;
            totalCost += half * halfVariant.price;
          }
          if (quarter > 0 && quarterVariant && quarterVariant.coverage > 0) {
            totalCoverage += quarter * quarterVariant.coverage;
            totalCost += quarter * quarterVariant.price;
          }
          
          // Only consider combinations that meet or exceed required area
          if (totalCoverage >= area) {
            const waste = totalCoverage - area; // Calculate waste
            validCombinations.push({
              breakdown: { full, half, quarter },
              coverage: totalCoverage,
              cost: totalCost,
              waste: waste
            });
          }
        }
      }
    }
    
    // If no valid combinations found, return empty breakdown
    if (validCombinations.length === 0) {
      // Fallback: use simple rounding up with available packs
      if (fullVariant && fullVariant.coverage > 0) {
        breakdown.full = Math.ceil(area / fullVariant.coverage);
      } else if (halfVariant && halfVariant.coverage > 0) {
        breakdown.half = Math.ceil(area / halfVariant.coverage);
      } else if (quarterVariant && quarterVariant.coverage > 0) {
        breakdown.quarter = Math.ceil(area / quarterVariant.coverage);
      }
      return breakdown;
    }
    
    // Sort combinations by:
    // 1. Lowest cost (primary)
    // 2. Least waste (secondary, for tie-breaking)
    validCombinations.sort((a, b) => {
      if (a.cost !== b.cost) {
        return a.cost - b.cost; // Lower cost first
      }
      return a.waste - b.waste; // Less waste first (if same cost)
    });
    
    // Return the best combination (first in sorted array)
    return validCombinations[0]?.breakdown || { full: 0, half: 0, quarter: 0 };
  }

  /**
   * Calculate costs for each pack type
   * Returns: { full: 0, half: 0, quarter: 0, total: 0 } (all in pence)
   */
  /** @param {PackBreakdown} breakdown */
  calculatePackCosts(breakdown) {
    /** @type {PackCosts} */
    const costs = { full: 0, half: 0, quarter: 0, total: 0 };
    const packTypes = /** @type {PackType[]} */ (['full', 'half', 'quarter']);
    
    for (const packType of packTypes) {
      const variant = this.variantMap?.[packType];
      const quantity = breakdown[packType] || 0;
      
      if (variant && quantity > 0) {
        costs[packType] = quantity * variant.price; // Price in pence
        costs.total += costs[packType];
      }
    }
    
    return costs;
  }

  /**
   * Update area display
   */
  /** @param {number} totalArea */
  updateAreaDisplay(totalArea) {
    const totalAreaDisplay = this.refs.totalAreaDisplay;
    if (totalAreaDisplay && !Array.isArray(totalAreaDisplay) && totalAreaDisplay instanceof HTMLElement) {
      totalAreaDisplay.textContent = `${totalArea.toFixed(2)} m²`;
    }
  }

  /**
   * Update pack breakdown display
   */
  /** @param {PackBreakdown} breakdown */
  updatePackDisplay(breakdown) {
    const packsDisplay = this.refs.packsDisplay;
    const packsLoading = this.refs.packsLoading;
    
    if (!packsDisplay || Array.isArray(packsDisplay) || !(packsDisplay instanceof HTMLElement)) {
      return;
    }
    
    // Hide loading
    if (packsLoading && !Array.isArray(packsLoading) && packsLoading instanceof HTMLElement) {
      packsLoading.style.display = 'none';
    }
    
    // Clear existing pack rows (except loading)
    const existingRows = packsDisplay.querySelectorAll('.product-calculator__pack-row');
    existingRows.forEach(row => row.remove());
    
    // Add pack rows for each pack type that has quantity > 0
    const packOrder = /** @type {PackType[]} */ (['full', 'half', 'quarter']);
    /** @type {Record<PackType, string>} */
    const packLabels = { full: 'Full pack', half: 'Half pack', quarter: 'Quarter pack' };
    
    for (const packType of packOrder) {
      const quantity = breakdown[packType] || 0;
      if (quantity > 0) {
        const row = document.createElement('div');
        row.className = 'product-calculator__pack-row';
        
        const count = document.createElement('span');
        count.className = 'product-calculator__pack-count';
        count.textContent = String(quantity);
        
        const multiply = document.createElement('span');
        multiply.className = 'product-calculator__pack-multiply';
        multiply.textContent = '×';
        
        const label = document.createElement('span');
        label.className = 'product-calculator__pack-label';
        label.textContent = packLabels[packType] || packType;
        
        row.appendChild(count);
        row.appendChild(multiply);
        row.appendChild(label);
        packsDisplay.appendChild(row);
      }
    }
    
    // If no packs, show loading placeholder
    if (packsDisplay.children.length === 0 || 
        (packsDisplay.children.length === 1 && packsDisplay.children[0] === packsLoading)) {
      if (packsLoading && !Array.isArray(packsLoading) && packsLoading instanceof HTMLElement) {
        packsLoading.style.display = 'block';
      }
    }
  }

  /**
   * Update cost breakdown display
   */
  /** @param {PackBreakdown} breakdown @param {PackCosts} costs */
  updateCostDisplay(breakdown, costs) {
    const costBreakdown = this.refs.costBreakdown;
    const costLoading = this.refs.costLoading;
    
    if (!costBreakdown || Array.isArray(costBreakdown) || !(costBreakdown instanceof HTMLElement)) {
      return;
    }
    
    // Hide loading
    if (costLoading && !Array.isArray(costLoading) && costLoading instanceof HTMLElement) {
      costLoading.style.display = 'none';
    }
    
    // Clear existing cost rows (except loading)
    const existingRows = costBreakdown.querySelectorAll('.product-calculator__cost-row');
    existingRows.forEach(row => row.remove());
    
    // Add cost rows for each pack type that has cost > 0
    const packOrder = /** @type {PackType[]} */ (['full', 'half', 'quarter']);
    /** @type {Record<PackType, string>} */
    const packLabels = { full: 'Full packs', half: 'Half packs', quarter: 'Quarter packs' };
    
    for (const packType of packOrder) {
      const quantity = breakdown[packType] || 0;
      const cost = costs[packType] || 0;
      
      if (quantity > 0 && cost > 0) {
        const variant = this.variantMap?.[packType];
        if (!variant) continue;
        
        const row = document.createElement('div');
        row.className = 'product-calculator__cost-row';
        
        const label = document.createElement('div');
        label.className = 'product-calculator__cost-label';
        label.textContent = packLabels[packType] || packType;
        
        const calculation = document.createElement('div');
        calculation.className = 'product-calculator__cost-calculation';
        calculation.textContent = `${quantity} × ${this.formatPrice(variant.price)}`;
        
        const value = document.createElement('div');
        value.className = 'product-calculator__cost-value';
        value.textContent = this.formatPrice(cost);
        
        row.appendChild(label);
        row.appendChild(calculation);
        row.appendChild(value);
        costBreakdown.appendChild(row);
      }
    }
    
    // If no costs, show loading placeholder
    if (costBreakdown.children.length === 0 || 
        (costBreakdown.children.length === 1 && costBreakdown.children[0] === costLoading)) {
      if (costLoading && !Array.isArray(costLoading) && costLoading instanceof HTMLElement) {
        costLoading.style.display = 'block';
      }
    }
  }

  /**
   * Update total price display (pack-based)
   */
  /** @param {number} totalCost */
  updateTotalPriceDisplay(totalCost) {
    const costDisplay = this.refs.costDisplay;
    if (costDisplay && !Array.isArray(costDisplay) && costDisplay instanceof HTMLElement) {
      const costValue = Number(totalCost) || 0;
      if (costValue > 0) {
        costDisplay.textContent = this.formatPrice(costValue);
      } else {
        costDisplay.textContent = '—';
      }
    }
  }

  /**
   * Update m² price unit display (price per m² from metafield)
   */
  updateM2PriceUnitDisplay() {
    const pricePerM2UnitDisplay = this.refs.pricePerM2UnitDisplay;
    if (pricePerM2UnitDisplay && !Array.isArray(pricePerM2UnitDisplay) && pricePerM2UnitDisplay instanceof HTMLElement) {
      const pricePerM2Value = this.pricePerM2 || 0;
      if (pricePerM2Value > 0) {
        // Convert pounds to pence for formatting
        const priceInPence = pricePerM2Value * 100;
        pricePerM2UnitDisplay.textContent = this.formatPrice(priceInPence) + ' / m²';
      } else {
        pricePerM2UnitDisplay.textContent = '—';
      }
    }
  }

  /**
   * Update m² price display (total cost based on area)
   */
  /** @param {number} totalM2Cost */
  updateM2PriceDisplay(totalM2Cost) {
    const pricePerM2Display = this.refs.pricePerM2Display;
    if (pricePerM2Display && !Array.isArray(pricePerM2Display) && pricePerM2Display instanceof HTMLElement) {
      const costValue = Number(totalM2Cost) || 0;
      if (costValue > 0) {
        // Convert pounds to pence for formatting
        const costInPence = costValue * 100;
        pricePerM2Display.textContent = this.formatPrice(costInPence);
      } else {
        pricePerM2Display.textContent = '—';
      }
    }
  }

  /**
   * Format price from pence to currency string
   */
  /** @param {number} priceInPence */
  formatPrice(priceInPence) {
    const priceValue = typeof priceInPence === 'number' ? priceInPence : parseFloat(String(priceInPence)) || 0;
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: this.currency
    }).format(priceValue / 100);
  }

  showSkeleton() {
    const skeletonContainer = this.refs.skeletonContainer;
    if (skeletonContainer && !Array.isArray(skeletonContainer) && skeletonContainer instanceof HTMLElement) {
      skeletonContainer.style.display = 'flex';
      skeletonContainer.offsetHeight; // Force reflow
      requestAnimationFrame(() => {
        skeletonContainer.style.opacity = '1';
      });
    }
  }

  hideSkeleton() {
    const skeletonContainer = this.refs.skeletonContainer;
    if (skeletonContainer && !Array.isArray(skeletonContainer) && skeletonContainer instanceof HTMLElement) {
      skeletonContainer.style.opacity = '0';
      setTimeout(() => {
        if (skeletonContainer.style.opacity === '0') {
          skeletonContainer.style.display = 'none';
        }
      }, 200);
    }
  }

  showLoadingState() {
    // Set all displays to placeholder
    const totalAreaDisplay = this.refs.totalAreaDisplay;
    const pricePerM2UnitDisplay = this.refs.pricePerM2UnitDisplay;
    const pricePerM2Display = this.refs.pricePerM2Display;
    const costDisplay = this.refs.costDisplay;
    const packsLoading = this.refs.packsLoading;
    const costLoading = this.refs.costLoading;
    const deliveryCta = this.refs.deliveryCta;
    
    if (totalAreaDisplay && !Array.isArray(totalAreaDisplay) && totalAreaDisplay instanceof HTMLElement) {
      totalAreaDisplay.textContent = '—';
    }
    
    if (pricePerM2UnitDisplay && !Array.isArray(pricePerM2UnitDisplay) && pricePerM2UnitDisplay instanceof HTMLElement) {
      pricePerM2UnitDisplay.textContent = '—';
    }
    
    if (pricePerM2Display && !Array.isArray(pricePerM2Display) && pricePerM2Display instanceof HTMLElement) {
      pricePerM2Display.textContent = '—';
    }
    
    if (costDisplay && !Array.isArray(costDisplay) && costDisplay instanceof HTMLElement) {
      costDisplay.textContent = '—';
    }
    
    // Show loading placeholders
    if (packsLoading && !Array.isArray(packsLoading) && packsLoading instanceof HTMLElement) {
      packsLoading.style.display = 'block';
    }
    
    if (costLoading && !Array.isArray(costLoading) && costLoading instanceof HTMLElement) {
      costLoading.style.display = 'block';
    }
    
    // Clear dynamic pack and cost rows
    const packsDisplay = this.refs.packsDisplay;
    if (packsDisplay && !Array.isArray(packsDisplay) && packsDisplay instanceof HTMLElement) {
      const existingRows = packsDisplay.querySelectorAll('.product-calculator__pack-row');
      existingRows.forEach(row => row.remove());
    }
    
    const costBreakdown = this.refs.costBreakdown;
    if (costBreakdown && !Array.isArray(costBreakdown) && costBreakdown instanceof HTMLElement) {
      const existingRows = costBreakdown.querySelectorAll('.product-calculator__cost-row');
      existingRows.forEach(row => row.remove());
    }

    // Reset CTAs
    if (deliveryCta && deliveryCta instanceof HTMLButtonElement) {
      deliveryCta.disabled = true;
      deliveryCta.classList.add('is-disabled');
    }
    this.toggleDeliverySection(false);
    this.setAddToCartEnabled(false);
    this.clearActionMessage();
    this.updateDeliveryPricingDisplay(0, 0);
  }

  getCalculatedQuantity() {
    if (!this.calculatedPacks) return 0;
    return (this.calculatedPacks.full || 0) + 
           (this.calculatedPacks.half || 0) + 
           (this.calculatedPacks.quarter || 0);
  }

  /**
   * Whether we have a valid calculation to proceed with delivery/cart steps
   */
  hasValidCalculation() {
    return Boolean(
      this.calculatedArea &&
      this.calculatedArea > 0 &&
      this.calculatedPacks &&
      (this.calculatedPacks.full || 0) + (this.calculatedPacks.half || 0) + (this.calculatedPacks.quarter || 0) > 0
    );
  }

  /**
   * Enable/disable CTAs based on calculator validity
   * @param {boolean} isValid
   */
  updateActionState(isValid) {
    const deliveryCta = this.refs.deliveryCta;
    const addToCartButton = this.refs.addToCartButton;

    if (deliveryCta && deliveryCta instanceof HTMLButtonElement) {
      deliveryCta.disabled = !isValid;
      deliveryCta.classList.toggle('is-disabled', !isValid);
    }

    if (!isValid) {
      this.toggleDeliverySection(false);
      this.setAddToCartEnabled(false);
    } else {
      this.clearActionMessage();
    }

    if (addToCartButton && addToCartButton instanceof HTMLButtonElement && !this.deliveryState.zone) {
      addToCartButton.disabled = true;
      addToCartButton.classList.add('is-disabled');
    }
  }

  /**
   * Show or hide delivery section
   * @param {boolean} show
   */
  toggleDeliverySection(show) {
    const deliverySection = this.refs.deliverySection;
    if (deliverySection && deliverySection instanceof HTMLElement) {
      deliverySection.style.display = show ? 'block' : 'none';
      // Update postcode field state when section is shown
      if (show) {
        this.updatePostcodeFieldState();
      } else {
        // Hide breakdown when section is hidden
        this.hidePricingBreakdown();
      }
    }
  }

  /**
   * Handle first CTA click to reveal delivery section
   */
  handleDeliveryStart() {
    if (!this.hasValidCalculation()) {
      this.setActionMessage('Please enter length & width to calculate first.');
      this.setAddToCartEnabled(false);
      this.toggleDeliverySection(false);
      return;
    }
    this.clearActionMessage();
    this.toggleDeliverySection(true);

    // Focus on date field first (not postcode)
    const deliveryDateInput = this.refs.deliveryDateInput;
    if (deliveryDateInput && deliveryDateInput instanceof HTMLInputElement) {
      // Small delay to ensure modal/section is visible
      setTimeout(() => {
        deliveryDateInput.focus();
        // Try to open date picker if supported
        if (typeof deliveryDateInput.showPicker === 'function') {
          deliveryDateInput.showPicker();
        }
      }, 100);
    }

    // Disable postcode until date is selected
    this.updatePostcodeFieldState();
  }

  /**
   * Validate postcode and determine delivery zone
   */
  handleDeliveryCheck(isAuto = false) {
    if (!this.hasValidCalculation()) {
      if (!isAuto) {
        this.setActionMessage('Please calculate area before checking delivery.');
        this.setAddToCartEnabled(false);
      }
      return;
    }

    const postcodeInput = this.refs.deliveryPostcodeInput;
    const deliveryTypeSelect = this.refs.deliveryTypeSelect;
    const deliveryDateInput = this.refs.deliveryDateInput;
    if (!postcodeInput || !(postcodeInput instanceof HTMLInputElement)) {
      if (!isAuto) this.setActionMessage('Enter a valid postcode.');
      return;
    }

    const rawPostcode = postcodeInput.value || '';
    const cleanedPostcode = this.sanitizePostcode(rawPostcode);
    const zone = this.determineZone(cleanedPostcode);
    const type = (deliveryTypeSelect && deliveryTypeSelect instanceof HTMLSelectElement && deliveryTypeSelect.value) ? deliveryTypeSelect.value : 'standard';
    const selectedDate = deliveryDateInput && deliveryDateInput instanceof HTMLInputElement ? deliveryDateInput.value : '';

    // Require delivery date before calculating
    if (!selectedDate) {
      if (!isAuto) this.setActionMessage('Please select a delivery date first.');
      this.setAddToCartEnabled(false);
      return;
    }

    // Validate postcode format first
    if (!cleanedPostcode) {
      if (!isAuto) {
        this.setActionMessage('Please enter a postcode.');
        this.setDeliveryResult('Please enter a postcode to check delivery.', 'error');
        this.setAddToCartEnabled(false);
        this.hidePricingBreakdown();
      }
      return;
    }

    // Check if postcode format is valid
    if (!this.isValidUKPostcodeFormat(cleanedPostcode)) {
      if (!isAuto) {
        this.setActionMessage('Invalid postcode format.');
        this.setDeliveryResult('Invalid postcode. Please enter a valid UK postcode (e.g., SW1A 1AA, M1 1AA).', 'error');
        this.setAddToCartEnabled(false);
        this.hidePricingBreakdown();
      }
      return;
    }

    // Check if postcode matches any zone
    if (!zone) {
      if (!isAuto) {
        this.setActionMessage('Postcode not serviceable.');
        this.setDeliveryResult('This postcode is not currently serviceable. Please contact us for delivery options.', 'error');
        this.setAddToCartEnabled(false);
        this.hidePricingBreakdown();
      }
      return;
    }

    // Zone 4 - Contact Us (not available for delivery)
    if (zone === 'Zone 4') {
      this.setDeliveryResult(
        'Delivery not available to this area. Please contact us for shipping options.',
        'error'
      );
      this.setActionMessage('Delivery unavailable. Please contact us for shipping options.');
      this.updateDeliveryPricingDisplay(0, this.calculatedCost || 0);
      this.setAddToCartEnabled(false);
      
      // Disable delivery type select for Zone 4
      if (deliveryTypeSelect && deliveryTypeSelect instanceof HTMLSelectElement) {
        deliveryTypeSelect.disabled = true;
      }
      
      // Prevent checkout for Zone 4
      this.preventCheckoutForZone4();
      
      this.deliveryState = {
        postcode: cleanedPostcode,
        zone: 'Zone 4',
        type: '',
        estimated: 'Contact us',
        cost: 0,
        date: selectedDate
      };
      return;
    }

    // Re-enable delivery type select for other zones
    if (deliveryTypeSelect && deliveryTypeSelect instanceof HTMLSelectElement) {
      deliveryTypeSelect.disabled = false;
    }

    // Use static guide rates since we are not adding to cart before lookup
    const effectiveType = this.isWeekendDate(selectedDate) ? 'weekend' : type;
    const deliveryCost = this.estimateDeliveryCost(effectiveType, zone, selectedDate);

    const totalWithDelivery = (this.calculatedCost || 0) + (deliveryCost || 0);

    this.deliveryState = {
      postcode: cleanedPostcode,
      zone,
      type: effectiveType,
      estimated: this.estimateDeliveryTime(effectiveType, zone, selectedDate),
      cost: deliveryCost,
      date: selectedDate
    };

    // Format delivery message with pack count
    const packCount = this.getTotalPackCount();
    const packText = packCount === 1 ? 'pack' : 'packs';
    let deliveryMessage = `Delivery available in ${zone} (${effectiveType}).`;
    
    if (deliveryCost > 0) {
      deliveryMessage += ` Delivery cost: ${this.formatPrice(deliveryCost)}`;
      if (zone !== 'Zone 1' || effectiveType !== 'standard') {
        deliveryMessage += ` (${packCount} ${packText})`;
      }
      deliveryMessage += ' — This price will be included in your cart.';
    } else {
      deliveryMessage += ' Free delivery — No delivery charge.';
    }

    this.setDeliveryResult(deliveryMessage, 'success');
    
    // Show pricing breakdown
    this.showPricingBreakdown(zone, effectiveType, deliveryCost, packCount);
    
    this.updateDeliveryPricingDisplay(deliveryCost, totalWithDelivery);
    this.clearActionMessage();
    this.setAddToCartEnabled(true);
  }

  /**
   * Add to cart with calculated variants and cart attributes
   */
  async handleAddToCart() {
    if (!this.hasValidCalculation()) {
      this.setActionMessage('Please calculate area before adding to cart.');
      this.setAddToCartEnabled(false);
      return;
    }

    if (!this.deliveryState.zone || !this.deliveryState.postcode || !this.deliveryState.date) {
      this.setActionMessage('Please select a delivery date and confirm delivery before adding to cart.');
      this.setAddToCartEnabled(false);
      return;
    }
    
    // Validate delivery date (min date, blocked holiday, Sunday) from current input
    const currentDateValue = this.refs.deliveryDateInput && this.refs.deliveryDateInput instanceof HTMLInputElement ? this.refs.deliveryDateInput.value : '';
    if (!currentDateValue || !this.validateDeliveryDate(currentDateValue)) {
      this.setAddToCartEnabled(false);
      return;
    }
    
    // Validate delivery date is not a blocked holiday
    const deliveryDateInput = this.refs.deliveryDateInput;
    if (deliveryDateInput && deliveryDateInput instanceof HTMLInputElement && deliveryDateInput.value) {
      const holidayCheck = this.isDateBlocked(deliveryDateInput.value);
      if (holidayCheck.blocked) {
        const errorMessage = `Delivery is not available on ${holidayCheck.holidayName}. Please select another date.`;
        this.setActionMessage(errorMessage);
        this.setAddToCartEnabled(false);
        this.showDeliveryDateError(errorMessage);
        deliveryDateInput.focus();
        if (typeof deliveryDateInput.showPicker === 'function') {
          deliveryDateInput.showPicker();
        }
        return;
      }
      
      // Also check for Sunday
      const dayOfWeek = this.getDayOfWeek(deliveryDateInput.value);
      if (dayOfWeek === 0) {
        const errorMessage = 'Sunday delivery is not available. Please select a different date.';
        this.setActionMessage(errorMessage);
        this.setAddToCartEnabled(false);
        this.showDeliveryDateError(errorMessage);
        deliveryDateInput.focus();
        if (typeof deliveryDateInput.showPicker === 'function') {
          deliveryDateInput.showPicker();
        }
        return;
      }
    }
    
    // Prevent adding to cart for Zone 4
    if (this.deliveryState.zone === 'Zone 4') {
      this.setActionMessage('Delivery not available to this area. Please contact us for shipping options.');
      this.setAddToCartEnabled(false);
      return;
    }

    const payload = this.prepareCartPayload();
    if (!payload.items.length) {
      this.setActionMessage('No variants to add. Please try again.');
      return;
    }

    this.setAddToCartLoading(true);
    try {
      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: payload.items })
      });

      if (!addRes.ok) {
        const errData = await addRes.json().catch(() => ({}));
        const message = errData?.message || errData?.description || 'Could not add to cart.';
        throw new Error(message);
      }

      const updateRes = await fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ attributes: payload.attributes })
      });

      if (!updateRes.ok) {
        const errData = await updateRes.json().catch(() => ({}));
        const message = errData?.message || errData?.description || 'Could not update cart.';
        throw new Error(message);
      }

      const cartState = await this.refreshCartUI();
      const cartJson = cartState?.cart || null;
      const sections = cartState?.sections || {};

      // Check for Zone 4 and prevent checkout if needed
      if (cartJson) {
        this.checkCartForZone4(cartJson);
      }

      this.setActionMessage('Items added. Delivery cost included in cart total.', 'success');

      const eventData = {
        sections,
        itemCount: cartJson?.item_count,
        source: 'product-calculator',
        response: { sections }
      };

      // Theme-native cart events
      const addEvent = new CartAddEvent(cartJson, 'product-calculator', eventData);
      const updateEvent = new CartUpdateEvent(cartJson || {}, 'product-calculator', eventData);
      document.dispatchEvent(addEvent);
      document.dispatchEvent(updateEvent);

      // Legacy/generic events for broader compatibility
      const detail = { cartUpdated: true, cart: cartJson, sections, response: { sections } };
      this.dispatchEvent(new CustomEvent('calculator:added-to-cart', { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail }));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail }));
      document.dispatchEvent(new CustomEvent('cart:change', { detail }));
      document.dispatchEvent(new CustomEvent('cart:update', { detail }));
      document.dispatchEvent(new CustomEvent('cart:request-complete', { detail }));
      document.dispatchEvent(new CustomEvent('cart:reload', { detail }));

      // Shopify core callback if present
      // @ts-ignore
      if (typeof window !== 'undefined' && window.Shopify && typeof window.Shopify.onCartUpdate === 'function' && cartJson) {
        // @ts-ignore
        window.Shopify.onCartUpdate(cartJson);
      }
      this.updateCartStateUI(cartJson);
      
      // Update cart total to include delivery cost
      this.updateCartTotalWithDelivery(cartJson);
    } catch (error) {
      console.error('Add to cart failed', error);
      const message = error instanceof Error ? error.message : 'Could not add to cart. Please try again.';
      this.setActionMessage(message);
    } finally {
      this.setAddToCartLoading(false);
    }
  }

  /**
   * Build cart payload with line item properties and cart attributes
   */
  prepareCartPayload() {
    /** @type {{items: Array<any>, attributes: Record<string, string>}} */
    const result = { items: [], attributes: {} };

    const breakdown = this.calculatedPacks || { full: 0, half: 0, quarter: 0 };
    const packOrder = /** @type {PackType[]} */ (['full', 'half', 'quarter']);

    const lineItemProps = this.buildLineItemProperties();

    // Add product pack items to cart
    for (const packType of packOrder) {
      const quantity = breakdown[packType] || 0;
      const variant = this.variantMap?.[packType];
      if (variant && quantity > 0) {
        result.items.push({
          id: variant.id,
          quantity,
          properties: lineItemProps
        });
      }
    }
    
    // Add delivery product as a separate line item (if not Zone 4 and delivery cost > 0)
    const zone = this.deliveryState.zone || '';
    const type = this.deliveryState.type || '';
    const deliveryCost = this.deliveryState.cost || 0;
    
    if (zone && zone !== 'Zone 4' && type && deliveryCost > 0) {
      const deliveryVariantId = this.getDeliveryVariantId(zone, type);
      
      if (deliveryVariantId) {
        // Get the delivery variant to check its price
        const deliveryVariant = this.deliveryVariants.find(v => v.id === deliveryVariantId);
        const variantPrice = deliveryVariant?.price || 0; // Price in pence
        
        // Calculate pack count for delivery quantity
        const packCount = this.getTotalPackCount();
        
        // Determine delivery quantity to match calculated cost
        // Strategy: Try to match variant price × quantity = calculated cost
        let deliveryQuantity = 1;
        
        if (variantPrice > 0) {
          // If variant price divides evenly into calculated cost, use that quantity
          const calculatedQuantity = Math.round(deliveryCost / variantPrice);
          if (calculatedQuantity > 0 && Math.abs((calculatedQuantity * variantPrice) - deliveryCost) < 1) {
            // Price matches when multiplied by calculated quantity (within 1 pence tolerance)
            deliveryQuantity = calculatedQuantity;
          } else if (Math.abs(variantPrice - deliveryCost) < 1) {
            // Variant price matches total cost exactly (flat rate)
            deliveryQuantity = 1;
          } else {
            // Fallback: use pack count for per-pack pricing
            // This assumes variant price is per-pack
            deliveryQuantity = packCount;
          }
        } else {
          // Free delivery - shouldn't reach here, but handle gracefully
          deliveryQuantity = 1;
        }
        
        // Final check: ensure quantity × price is close to calculated cost
        const totalVariantPrice = deliveryQuantity * variantPrice;
        const priceDifference = Math.abs(totalVariantPrice - deliveryCost);
        
        // If there's a significant difference (> 10 pence), log a warning
        if (priceDifference > 10) {
          console.warn(
            `Calculator: Delivery price mismatch. Calculated: ${deliveryCost}p, Variant: ${variantPrice}p × ${deliveryQuantity} = ${totalVariantPrice}p. Difference: ${priceDifference}p`
          );
        }
        
        result.items.push({
          id: deliveryVariantId,
          quantity: deliveryQuantity,
          properties: {
            'Delivery': `${zone} / ${type.charAt(0).toUpperCase() + type.slice(1)}`,
            'Delivery Zone': zone,
            'Delivery Type': type.charAt(0).toUpperCase() + type.slice(1),
            'Delivery Date': this.deliveryState.date || '',
            'Pack Count': String(packCount),
            '_delivery_zone': zone,
            '_delivery_type': type,
            '_delivery_date': this.deliveryState.date || '',
            '_calculated_delivery_cost_pence': String(deliveryCost),
            '_delivery_quantity': String(deliveryQuantity),
            '_pack_count': String(packCount),
            '_variant_price_pence': String(variantPrice),
            '_price_match': priceDifference <= 10 ? 'true' : 'false'
          }
        });
      } else {
        console.warn(`Calculator: Delivery variant not found for ${zone} / ${type}`);
      }
    } else if (zone && zone !== 'Zone 4' && type && deliveryCost === 0) {
      // Free delivery (Zone 1 Standard) - still add delivery product with price 0
      // This ensures cart validation passes and delivery details are tracked
      const deliveryVariantId = this.getDeliveryVariantId(zone, type);
      if (deliveryVariantId) {
        const deliveryVariant = this.deliveryVariants.find(v => v.id === deliveryVariantId);
        const variantPrice = deliveryVariant?.price || 0; // Should be 0 for Zone 1 Standard
        
        // Calculate pack count for delivery quantity
        const packCount = this.getTotalPackCount();
        
        // For free delivery, quantity should match pack count
        const deliveryQuantity = packCount;
        
        result.items.push({
          id: deliveryVariantId,
          quantity: deliveryQuantity,
          properties: {
            'Delivery': `${zone} / ${type.charAt(0).toUpperCase() + type.slice(1)}`,
            'Delivery Zone': zone,
            'Delivery Type': type.charAt(0).toUpperCase() + type.slice(1),
            'Delivery Date': this.deliveryState.date || '',
            'Pack Count': String(packCount),
            '_delivery_zone': zone,
            '_delivery_type': type,
            '_delivery_date': this.deliveryState.date || '',
            '_calculated_delivery_cost_pence': '0',
            '_delivery_quantity': String(deliveryQuantity),
            '_pack_count': String(packCount),
            '_variant_price_pence': String(variantPrice),
            '_price_match': 'true',
            '_free_delivery': 'true'
          }
        });
      } else {
        console.warn(`Calculator: Delivery variant not found for free delivery ${zone} / ${type}`);
      }
    }

    // Format delivery reference for cart attributes
    let deliveryReference = '';
    if (zone && type) {
      const formattedType = type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ');
      deliveryReference = `${zone} / ${formattedType}`;
    } else if (zone) {
      deliveryReference = zone;
    }
    
    result.attributes = {
      // Human-readable delivery reference
      delivery: deliveryReference || 'Not specified',
      
      // Detailed delivery information
      delivery_postcode: this.deliveryState.postcode || '',
      delivery_zone: zone,
      delivery_type: type,
      delivery_date: this.deliveryState.date || '',
      estimated_delivery_time: this.deliveryState.estimated || '',
      
      // Pricing information (final prices, not approximate)
      delivery_cost: this.deliveryState.cost ? this.formatPrice(this.deliveryState.cost) : '',
      delivery_cost_pence: this.deliveryState.cost ? String(this.deliveryState.cost) : '0',
      total_with_delivery: this.deliveryState.cost ? this.formatPrice((this.calculatedCost || 0) + this.deliveryState.cost) : '',
      total_with_delivery_pence: String((this.calculatedCost || 0) + (this.deliveryState.cost || 0)),
      // Note for checkout
      delivery_calculated_on_product_page: 'true',
      checkout_shipping_note: 'Delivery calculated on product page',
      
      // Internal references (for conditional logic and cart total calculation)
      _delivery_zone: zone,
      _delivery_type: type,
      _delivery_date: this.deliveryState.date || '',
      _delivery_cost_pence: this.deliveryState.cost ? String(this.deliveryState.cost) : '0',
      _delivery_cost_total: this.deliveryState.cost ? String(this.deliveryState.cost) : '0' // For cart summary calculation
    };

    return result;
  }

  /**
   * Properties to attach to each line item
   * Includes delivery information for display in cart
   */
  buildLineItemProperties() {
    const zone = this.deliveryState.zone || '';
    const type = this.deliveryState.type || '';
    const date = this.deliveryState.date || '';
    
    // Format delivery reference: "Zone 1 / Express" or "Zone 2 / Standard"
    let deliveryReference = '';
    if (zone && type) {
      // Capitalize first letter of type
      const formattedType = type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ');
      deliveryReference = `${zone} / ${formattedType}`;
    } else if (zone) {
      deliveryReference = zone;
    }
    
    // Format date for display (YYYY-MM-DD to readable format)
    let formattedDate = '';
    if (date) {
      try {
        const dateObj = new Date(date + 'T00:00:00');
        formattedDate = dateObj.toLocaleDateString('en-GB', { 
          weekday: 'short', 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        });
      } catch (e) {
        formattedDate = date;
      }
    }
    
    return {
      'Delivery': deliveryReference || 'Not specified',
      'Delivery Zone': zone || '',
      'Delivery Type': type ? type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ') : '',
      'Delivery Date': formattedDate || date || '',
      '_delivery_zone': zone, // Internal reference
      '_delivery_type': type, // Internal reference
      '_delivery_date': date // Internal reference (YYYY-MM-DD format)
    };
  }

  /**
   * Sanitize postcode input
   * @param {string} value
   */
  sanitizePostcode(value) {
    return (value || '').trim().toUpperCase();
  }

  /**
   * Keep postcode uppercase while typing without disrupting cursor focus.
   * Optionally inserts the inward-code space once enough characters are entered.
   * @param {HTMLInputElement} input
   * @param {{formatSpace?: boolean}} options
   * @returns {string}
   */
  normalizePostcodeInputValue(input, { formatSpace = false } = {}) {
    if (!(input instanceof HTMLInputElement)) return '';

    const selectionStart = input.selectionStart ?? input.value.length;
    const alphanumBeforeCursor = input.value
      .slice(0, selectionStart)
      .replace(/[^A-Za-z0-9]/g, '').length;

    const alphanum = input.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7);
    let formatted = alphanum;

    if (formatSpace && alphanum.length > 4) {
      formatted = `${alphanum.slice(0, -3)} ${alphanum.slice(-3)}`;
    } else if (!formatSpace) {
      formatted = input.value.toUpperCase().replace(/[^A-Z0-9 ]/g, '').replace(/\s+/g, ' ');
    }

    if (formatted !== input.value) {
      input.value = formatted;
      let newCursor = 0;
      let seen = 0;
      for (let i = 0; i < formatted.length; i++) {
        const char = formatted.charAt(i);
        if (/[A-Z0-9]/.test(char)) {
          seen++;
        }
        newCursor = i + 1;
        if (seen >= alphanumBeforeCursor) {
          break;
        }
      }
      input.setSelectionRange(newCursor, newCursor);
    }

    return formatted.trim();
  }

  /**
   * Shared postcode field handler: uppercase, debounced validation, no focus stealing.
   * @param {HTMLInputElement} input
   * @param {{formatSpace?: boolean, checkImmediately?: boolean}} options
   */
  handlePostcodeFieldInput(input, { formatSpace = false, checkImmediately = false } = {}) {
    this.normalizePostcodeInputValue(input, { formatSpace });
    this.hideDeliveryTypeFeedback();

    const deliveryDateInput = this.refs.deliveryDateInput;
    if (deliveryDateInput && deliveryDateInput instanceof HTMLInputElement && !deliveryDateInput.value) {
      this.setDeliveryResult('Please select a delivery date first.', 'error');
      return;
    }

    this.schedulePostcodeDeliveryCheck(checkImmediately);
  }

  /**
   * Validate delivery only once the postcode looks complete, so typing is uninterrupted.
   * @param {boolean} immediate
   */
  schedulePostcodeDeliveryCheck(immediate = false) {
    if (this.postcodeCheckTimeout) {
      clearTimeout(this.postcodeCheckTimeout);
    }
    const delay = immediate ? 0 : 350;

    this.postcodeCheckTimeout = setTimeout(() => {
      const postcodeInput = this.refs.deliveryPostcodeInput;
      if (!postcodeInput || !(postcodeInput instanceof HTMLInputElement)) return;

      const cleaned = this.sanitizePostcode(postcodeInput.value);
      if (!cleaned) return;

      if (this.isValidUKPostcodeFormat(cleaned)) {
        this.handleDeliveryCheck(true);
      }
    }, delay);
  }

  /**
   * UK Postcode Zone Mapping
   * Maps UK postcode prefixes to delivery zones
   * @returns {Record<string, string>}
   */
  getUKPostcodeZones() {
    /** @type {Record<string, string>} */
    return {
      // Zone 1 - Free Delivery (60+ prefixes)
      'AL': 'Zone 1',
      'B': 'Zone 1',
      'BB': 'Zone 1',
      'BD': 'Zone 1',
      'BR': 'Zone 1',
      'BL': 'Zone 1',
      'BS': 'Zone 1',
      'CB': 'Zone 1',
      'CF': 'Zone 1',
      'CH': 'Zone 1',
      'CV': 'Zone 1',
      'DE': 'Zone 1',
      'DN': 'Zone 1',
      'DY': 'Zone 1',
      'EX': 'Zone 1',
      'FY': 'Zone 1',
      'GL': 'Zone 1',
      'HD': 'Zone 1',
      'HG': 'Zone 1',
      'HR': 'Zone 1',
      'HU': 'Zone 1',
      'HX': 'Zone 1',
      'IP': 'Zone 1',
      'L': 'Zone 1',
      'LE': 'Zone 1',
      'LS': 'Zone 1',
      'LU': 'Zone 1',
      'M': 'Zone 1',
      'MK': 'Zone 1',
      'NG': 'Zone 1',
      'NN': 'Zone 1',
      'NP': 'Zone 1',
      'NR': 'Zone 1',
      'OL': 'Zone 1',
      'OX': 'Zone 1',
      'PE': 'Zone 1',
      'PR': 'Zone 1',
      'RG': 'Zone 1',
      'S': 'Zone 1',
      'SK': 'Zone 1',
      'SL': 'Zone 1',
      'SN': 'Zone 1',
      'SO': 'Zone 1',
      'SP': 'Zone 1',
      'ST': 'Zone 1',
      'TA': 'Zone 1',
      'TF': 'Zone 1',
      'TS': 'Zone 1',
      'WA': 'Zone 1',
      'WF': 'Zone 1',
      'WN': 'Zone 1',
      'WR': 'Zone 1',
      'WS': 'Zone 1',
      'WV': 'Zone 1',
      'YO': 'Zone 1',
      
      // Zone 2 - £10 Per Pack Surcharge (12 prefixes)
      'BN': 'Zone 2',
      'CA': 'Zone 2',
      'CT': 'Zone 2',
      'DT': 'Zone 2',
      'LA': 'Zone 2',
      'LD': 'Zone 2',
      'LL': 'Zone 2',
      'NE': 'Zone 2',
      'PL': 'Zone 2',
      'SA': 'Zone 2',
      'SY': 'Zone 2',
      'TN': 'Zone 2',
      'TQ': 'Zone 2',
      
      // Zone 3 - £20 Per Pack Surcharge (10 prefixes)
      'DG': 'Zone 3',
      'EH': 'Zone 3',
      'FK': 'Zone 3',
      'G': 'Zone 3',
      'KA': 'Zone 3',
      'KY': 'Zone 3',
      'ML': 'Zone 3',
      'PA': 'Zone 3',
      'TD': 'Zone 3',
      'TR': 'Zone 3',
      
      // Zone 4 - Contact Us / Not Available (11 prefixes)
      'AB': 'Zone 4',
      'BT': 'Zone 4',
      'DD': 'Zone 4',
      'EIRE': 'Zone 4',
      'GY': 'Zone 4',
      'HS': 'Zone 4',
      'IM': 'Zone 4',
      'IV': 'Zone 4',
      'JE': 'Zone 4',
      'KW': 'Zone 4',
      'PH': 'Zone 4',
      'ZE': 'Zone 4'
    };
  }

  /**
   * Validate UK postcode format
   * Valid formats: SW1A 1AA, B1 3RB, M1 1AA, SW1A1AA, B13RB, M11AA
   * @param {string} postcode
   * @returns {boolean} True if valid UK postcode format
   */
  isValidUKPostcodeFormat(postcode = '') {
    if (!postcode) return false;
    const cleaned = postcode.trim().toUpperCase().replace(/\s+/g, '');
    
    // UK postcode pattern: 1-2 letters, 1-2 numbers, optional letter, space (optional), number, 2 letters
    // Examples: SW1A1AA, B13RB, M11AA, SW1A 1AA, B1 3RB, M1 1AA
    const ukPostcodePattern = /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/;
    return ukPostcodePattern.test(cleaned);
  }

  /**
   * Extract postcode prefix from UK postcode
   * Handles formats like: SW1A 1AA, B1 3RB, M1 1AA
   * @param {string} postcode
   * @returns {string|null} Postcode prefix (1-2 letters) or null if invalid format
   */
  extractPostcodePrefix(postcode = '') {
    if (!postcode) return null;
    
    // First validate format
    if (!this.isValidUKPostcodeFormat(postcode)) return null;
    
    const cleaned = postcode.trim().toUpperCase().replace(/\s+/g, '');
    
    // Match UK postcode pattern: 1-2 letters followed by numbers
    const match = cleaned.match(/^([A-Z]{1,2})\d/);
    if (!match || !match[1]) return null;
    
    return match[1];
  }

  /**
   * Determine delivery zone from UK postcode
   * @param {string} postcode
   * @returns {string|null} Zone name or null if invalid
   */
  /** @param {string} postcode */
  determineZone(postcode = '') {
    if (!postcode || postcode.length < 2) return null;
    
    // Extract postcode prefix
    const prefix = this.extractPostcodePrefix(postcode);
    if (!prefix) return null;
    
    // Get zone mapping
    const zones = this.getUKPostcodeZones();
    
    // Check for 2-letter prefix first (e.g., "SW", "BN")
    if (prefix.length >= 2 && zones[prefix]) {
      return zones[prefix];
    }
    
    // Check for 1-letter prefix (e.g., "B", "M", "S")
    if (prefix.length === 1 && zones[prefix]) {
      return zones[prefix];
    }
    
    // Not found in any zone
    return null;
  }

  /**
   * Estimate delivery time based on delivery type
   * Standard: 3 working days + 1 day pick/pack = 4 working days minimum
   * Express: 1 day + 1 day pick/pack = 2 days minimum
   * Saturday: Weekend delivery
   * @param {string} type
   * @param {string} zone
   * @param {string} selectedDate
   */
  estimateDeliveryTime(type, zone, selectedDate = '') {
    const isWeekend = this.isWeekendDate(selectedDate);
    const lowered = (type || '').toLowerCase();
    
    switch (lowered) {
      case 'express':
        return '1-2 working days';
      case 'weekend':
      case 'saturday':
        return isWeekend ? 'Saturday delivery' : 'Saturday delivery (select Sat/Sun)';
      case 'standard':
      default:
        return '3-5 working days';
    }
  }

  /**
   * Fetch live shipping rates from Shopify AJAX API using postcode
   * @param {string} postcode
   * @returns {Promise<Array<{name:string, price:number}>>}
   */
  async fetchLiveShippingRates(postcode) {
    const params = new URLSearchParams({
      'shipping_address[zip]': postcode,
      'shipping_address[country]': 'United Kingdom'
    });
    const response = await fetch(`/cart/shipping_rates.json?${params.toString()}`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    });
    if (!response.ok) {
      throw new Error(`Shipping rates request failed: ${response.status}`);
    }
    const data = await response.json();
    const rates = Array.isArray(data?.shipping_rates) ? data.shipping_rates : [];
    return rates.map((/** @type {{name:string, price:number}} */ rate) => ({
      name: rate.name || '',
      price: this.parsePriceToPence(rate.price)
    }));
  }

  /**
   * Select a rate that matches the chosen delivery type
   * @param {ShippingRate[]} rates
   * @param {string} type
   * @param {string} selectedDate
   */
  pickRateForType(rates, type, selectedDate = '') {
    if (!Array.isArray(rates) || !rates.length) return null;
    /** @type {ShippingRate[]} */
    const safeRates = rates;
    const lowered = type.toLowerCase();
    const isWeekend = this.isWeekendDate(selectedDate);

    /**
     * @param {ShippingRate[]} list
     * @param {string[]} keywords
     * @returns {ShippingRate | null}
     */
    function findByKeyword(list, keywords) {
      /** @type {ShippingRate[]} */
      const typedList = list;
      for (let i = 0; i < typedList.length; i += 1) {
        const rate = typedList[i];
        if (!rate) continue;
        const name = (rate.name || '').toLowerCase();
        if (keywords.some((k) => name.includes(k))) return rate;
      }
      return null;
    }

    if (lowered === 'weekend') {
      // Weekend: prefer names with "weekend"; if weekend date, allow Saturday/Sunday wording
      const match = findByKeyword(safeRates, ['weekend', 'sat', 'sun']);
      if (match) return match;
    }

    if (lowered === 'next-day' || lowered === 'next day') {
      const match = findByKeyword(safeRates, ['next day', 'next-day', 'nextday']);
      if (match) return match;
    }

    if (lowered === 'express') {
      const match = findByKeyword(safeRates, ['express']);
      if (match) return match;
    }

    // Standard fallback
    const standardMatch = findByKeyword(safeRates, ['standard']);
    if (standardMatch) return standardMatch;

    // Weekend special-case: if is weekend date, allow weekend rate
    if (isWeekend) {
      const weekendMatch = findByKeyword(safeRates, ['weekend', 'sat', 'sun']);
      if (weekendMatch) return weekendMatch;
    }

    // Finally, pick cheapest
    const cheapest = safeRates.slice().sort((a, b) => (a.price || 0) - (b.price || 0))[0];
    return cheapest || null;
  }

  /**
   * Parse a Shopify rate price (string/number) to pence
   * @param {string|number} value
   */
  parsePriceToPence(value) {
    if (typeof value === 'number') return Math.round(value * 100);
    const num = parseFloat(String(value || '0'));
    return Math.round(num * 100);
  }

  /**
   * Calculate total pack count (1 pack = 1 variant, regardless of full/half/quarter)
   * @returns {number} Total number of packs
   */
  getTotalPackCount() {
    if (!this.calculatedPacks) return 0;
    return (this.calculatedPacks.full || 0) + 
           (this.calculatedPacks.half || 0) + 
           (this.calculatedPacks.quarter || 0);
  }

  /**
   * Estimate delivery cost based on zone, delivery type, and pack count
   * Uses prices from the delivery product variants (delivery-paving-slabs)
   * Pricing is dynamically loaded from Shopify delivery product variants, not hardcoded.
   * 
   * Pricing structure (example - actual prices come from delivery product):
   * - Zone 1: Free (Standard), Express per pack, Weekend flat rate
   * - Zone 2: Standard per pack, Express per pack, Weekend flat rate
   * - Zone 3: Standard per pack, Express per pack, Weekend flat rate
   * - Zone 4: Not available (returns 0, should show "Contact Us")
   * 
   * Weekend/Saturday delivery uses variant price as flat rate (not multiplied by pack count).
   * Standard/Express delivery uses variant price × pack count.
   * 
   * @param {string} type - Delivery type: 'standard', 'express', 'weekend'/'saturday'
   * @param {string} zone - Zone name: 'Zone 1', 'Zone 2', 'Zone 3', 'Zone 4'
   * @param {string} selectedDate - Selected delivery date (YYYY-MM-DD)
   * @returns {number} Delivery cost in pence
   */
  estimateDeliveryCost(type, zone, selectedDate = '') {
    // Zone 4 - Not available
    if (zone === 'Zone 4') {
      return 0; // Will show "Contact Us" message instead
    }

    // Get total pack count (1 pack = 1 variant)
    const packCount = this.getTotalPackCount();
    if (packCount === 0) return 0;

    let lowered = (type || '').toLowerCase();
    if (!this.expressAvailable && lowered === 'express') {
      lowered = 'standard';
    }
    const isWeekend = this.isWeekendDate(selectedDate);
    const effectiveType = (lowered === 'weekend' || lowered === 'saturday' || isWeekend) ? 'weekend' : lowered;
    
    // Try to use delivery variant price first (from configured delivery product)
    const normalizedType = effectiveType === 'weekend' ? 'weekend' : effectiveType;
    const zoneMap = this.deliveryVariantMap[zone];
    if (zoneMap && zoneMap[normalizedType] && zoneMap[normalizedType].price !== undefined) {
      const variantPrice = zoneMap[normalizedType].price;
      
      // For weekend/Saturday, use variant price as flat rate (not multiplied by pack count)
      // This matches the behavior where weekend is a flat £75 regardless of zone
      if (normalizedType === 'weekend') {
        return variantPrice; // Flat rate from variant
      }
      
      // For other types, use variant price multiplied by pack count
      return variantPrice * packCount;
    }
    
    // Fallback to calculated pricing (if variant not found)
    // This should rarely happen if delivery product is configured correctly
    console.warn(`Calculator: Delivery variant not found for ${zone} / ${normalizedType}, using fallback pricing`);
    
    // Saturday/Weekend delivery - £75 flat for ALL zones (fallback)
    if (normalizedType === 'weekend') {
      return 7500; // £75 flat rate for all zones (no zone surcharge)
    }

    // Express delivery (fallback)
    if (normalizedType === 'express') {
      let costPerPack = 2000; // £20 per pack base
      
      if (zone === 'Zone 2') {
        costPerPack = 3000; // £30 per pack (£10 zone + £20 express)
      } else if (zone === 'Zone 3') {
        costPerPack = 4000; // £40 per pack (£20 zone + £20 express)
      }
      // Zone 1: £20 per pack only
      
      return costPerPack * packCount;
    }

    // Standard delivery (fallback)
    if (normalizedType === 'standard' || !normalizedType) {
      if (zone === 'Zone 1') {
        return 0; // Free delivery
      } else if (zone === 'Zone 2') {
        return 1000 * packCount; // £10 per pack
      } else if (zone === 'Zone 3') {
        return 2000 * packCount; // £20 per pack
      }
    }

    // Default: free (shouldn't reach here)
    return 0;
  }

  /**
   * Show pricing breakdown for delivery calculation
   * @param {string} zone
   * @param {string} type
   * @param {number} totalCost - Total cost in pence
   * @param {number} packCount
   */
  showPricingBreakdown(zone, type, totalCost, packCount) {
    const breakdownContainer = this.refs.deliveryBreakdown;
    const breakdownContent = this.refs.deliveryBreakdownContent;
    
    if (!breakdownContainer || !breakdownContent) return;
    
    const lowered = (type || '').toLowerCase();
    const normalizedType = (lowered === 'weekend' || lowered === 'saturday') ? 'weekend' : lowered;
    const packText = packCount === 1 ? 'pack' : 'packs';
    
    // Get variant prices from delivery product
    const zoneMap = this.deliveryVariantMap[zone];
    const currentVariant = zoneMap && zoneMap[normalizedType] ? zoneMap[normalizedType] : null;
    const currentPricePerPack = currentVariant?.price || 0;
    
    // Get standard variant price for comparison (to calculate zone surcharge for express)
    const standardVariant = zoneMap && zoneMap['standard'] ? zoneMap['standard'] : null;
    const standardPricePerPack = standardVariant?.price || 0;
    
    // Build breakdown HTML
    let breakdownHTML = '<div class="product-calculator__breakdown-item">';
    
    // Zone information
    breakdownHTML += `<div class="product-calculator__breakdown-line">`;
    breakdownHTML += `<span class="product-calculator__breakdown-label">Zone:</span>`;
    breakdownHTML += `<span class="product-calculator__breakdown-value">${zone}</span>`;
    breakdownHTML += `</div>`;
    
    // Pack count
    breakdownHTML += `<div class="product-calculator__breakdown-line">`;
    breakdownHTML += `<span class="product-calculator__breakdown-label">Packs:</span>`;
    breakdownHTML += `<span class="product-calculator__breakdown-value">${packCount} ${packText}</span>`;
    breakdownHTML += `</div>`;
    
    // Calculate breakdown based on zone and type using variant prices
    if (normalizedType === 'standard') {
      if (currentPricePerPack === 0) {
        // Free delivery (Zone 1 Standard)
        breakdownHTML += `<div class="product-calculator__breakdown-line">`;
        breakdownHTML += `<span class="product-calculator__breakdown-label">Standard delivery:</span>`;
        breakdownHTML += `<span class="product-calculator__breakdown-value">Free</span>`;
        breakdownHTML += `</div>`;
      } else {
        // Zone surcharge (Zone 2 or Zone 3 Standard)
        const zoneLabel = zone === 'Zone 2' ? 'Zone 2 surcharge' : 'Zone 3 surcharge';
        breakdownHTML += `<div class="product-calculator__breakdown-line">`;
        breakdownHTML += `<span class="product-calculator__breakdown-label">${zoneLabel}:</span>`;
        breakdownHTML += `<span class="product-calculator__breakdown-value">${this.formatPrice(currentPricePerPack)} × ${packCount} ${packText} = ${this.formatPrice(currentPricePerPack * packCount)}</span>`;
        breakdownHTML += `</div>`;
      }
    } else if (normalizedType === 'express') {
      // For Express, try to show breakdown if we can calculate zone surcharge
      // Express variant price might be total (zone + express) or just express
      if (standardPricePerPack > 0 && currentPricePerPack > standardPricePerPack) {
        // Can show breakdown: zone surcharge + express
        const zoneSurcharge = standardPricePerPack;
        const expressSurcharge = currentPricePerPack - standardPricePerPack;
        
        if (zoneSurcharge > 0) {
          const zoneLabel = zone === 'Zone 2' ? 'Zone 2 surcharge' : 'Zone 3 surcharge';
          breakdownHTML += `<div class="product-calculator__breakdown-line">`;
          breakdownHTML += `<span class="product-calculator__breakdown-label">${zoneLabel}:</span>`;
          breakdownHTML += `<span class="product-calculator__breakdown-value">${this.formatPrice(zoneSurcharge)} × ${packCount} ${packText} = ${this.formatPrice(zoneSurcharge * packCount)}</span>`;
          breakdownHTML += `</div>`;
        }
        
        if (expressSurcharge > 0) {
          breakdownHTML += `<div class="product-calculator__breakdown-line">`;
          breakdownHTML += `<span class="product-calculator__breakdown-label">Express delivery:</span>`;
          breakdownHTML += `<span class="product-calculator__breakdown-value">${this.formatPrice(expressSurcharge)} × ${packCount} ${packText} = ${this.formatPrice(expressSurcharge * packCount)}</span>`;
          breakdownHTML += `</div>`;
        }
      } else {
        // Show total express price (Zone 1 Express or can't calculate breakdown)
        breakdownHTML += `<div class="product-calculator__breakdown-line">`;
        breakdownHTML += `<span class="product-calculator__breakdown-label">Express delivery:</span>`;
        breakdownHTML += `<span class="product-calculator__breakdown-value">${this.formatPrice(currentPricePerPack)} × ${packCount} ${packText} = ${this.formatPrice(currentPricePerPack * packCount)}</span>`;
        breakdownHTML += `</div>`;
      }
    } else if (normalizedType === 'weekend') {
      // Weekend delivery is flat rate (not per pack)
      breakdownHTML += `<div class="product-calculator__breakdown-line">`;
      breakdownHTML += `<span class="product-calculator__breakdown-label">Saturday delivery:</span>`;
      breakdownHTML += `<span class="product-calculator__breakdown-value">${this.formatPrice(currentPricePerPack)} (flat rate)</span>`;
      breakdownHTML += `</div>`;
      // Weekend delivery is flat rate with no zone surcharge
    }
    
    // Total
    breakdownHTML += `<div class="product-calculator__breakdown-line product-calculator__breakdown-line--total">`;
    breakdownHTML += `<span class="product-calculator__breakdown-label">Total delivery:</span>`;
    breakdownHTML += `<span class="product-calculator__breakdown-value">${totalCost > 0 ? this.formatPrice(totalCost) : 'Free'}</span>`;
    breakdownHTML += `</div>`;
    
    breakdownHTML += '</div>';
    
    if (breakdownContent && breakdownContent instanceof HTMLElement) {
      breakdownContent.innerHTML = breakdownHTML;
    }
    if (breakdownContainer && breakdownContainer instanceof HTMLElement) {
      breakdownContainer.style.display = 'block';
    }
  }

  /**
   * Hide pricing breakdown
   */
  hidePricingBreakdown() {
    const breakdownContainer = this.refs.deliveryBreakdown;
    if (breakdownContainer && breakdownContainer instanceof HTMLElement) {
      breakdownContainer.style.display = 'none';
    }
  }

  /**
   * Update cart total display to include delivery cost
   * @param {any} cartJson - Cart data from Shopify
   */
  updateCartTotalWithDelivery(cartJson) {
    if (!cartJson || !this.deliveryState.cost) return;
    
    const deliveryCostPence = this.deliveryState.cost || 0;
    if (deliveryCostPence === 0) return;
    
    // Get cart total from Shopify (in cents)
    const cartTotalCents = cartJson.total_price || 0;
    
    // Add delivery cost to cart total
    const totalWithDeliveryCents = cartTotalCents + deliveryCostPence;
    
    // Format the new total
    const formattedTotal = this.formatPrice(totalWithDeliveryCents);
    
    // Update cart total display elements
    const cartTotalElements = document.querySelectorAll('[ref="cartTotal"], [data-cart-subtotal], .cart__total-value');
    cartTotalElements.forEach((/** @type {HTMLElement} */ element) => {
      if (element instanceof HTMLElement) {
        // Update text content
        const textComponent = element.querySelector('text-component') || element;
        if (textComponent instanceof HTMLElement) {
          textComponent.textContent = formattedTotal;
          if (textComponent.hasAttribute('value')) {
            textComponent.setAttribute('value', formattedTotal.replace(/[£,\s]/g, ''));
          }
        } else {
          element.textContent = formattedTotal;
        }
      }
    });
    
    // Also update any cart total displays in the page
    const cartTotalDisplays = document.querySelectorAll('.cart-total, [data-cart-total]');
    cartTotalDisplays.forEach((/** @type {HTMLElement} */ element) => {
      if (element instanceof HTMLElement) {
        element.textContent = formattedTotal;
      }
    });
  }

  /**
   * Utility to check if a date string (YYYY-MM-DD) falls on weekend (Saturday or Sunday)
   * @param {string} dateStr
   */
  isWeekendDate(dateStr = '') {
    if (!dateStr) return false;
    // Parse date in local timezone to avoid UTC issues
    const parts = dateStr.split('-').map(Number);
    if (parts.length !== 3 || !parts[0] || !parts[1] || !parts[2]) return false;
    const [year, month, day] = parts;
    const date = new Date(year, month - 1, day); // month is 0-indexed
    const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
    // Weekend is Saturday (6) or Sunday (0)
    return dayOfWeek === 0 || dayOfWeek === 6;
  }

  /**
   * Check if a date is a working day (Monday-Friday)
   * @param {Date} date
   * @returns {boolean}
   */
  isWorkingDay(date) {
    const day = date.getDay();
    return day >= 1 && day <= 5; // Monday (1) to Friday (5)
  }

  /**
   * Add working days to a date (excludes weekends)
   * @param {Date} startDate
   * @param {number} workingDays
   * @returns {Date}
   */
  addWorkingDays(startDate, workingDays) {
    const result = new Date(startDate);
    let daysAdded = 0;
    
    while (daysAdded < workingDays) {
      result.setDate(result.getDate() + 1);
      if (this.isWorkingDay(result)) {
        daysAdded++;
      }
    }
    
    return result;
  }

  /**
   * Calculate first available delivery date based on delivery type
   * Express: Today + 1 day (pick/pack) + 1 day = 2 days minimum
   * Standard: Today + 1 day (pick/pack) + 3 working days = 4 working days minimum
   * Saturday: Can be any Saturday/Sunday from first available
   * 
   * For the date picker min attribute, we use the earliest possible (Express = 2 days)
   * so users can select express delivery dates, but standard will show appropriate lead time
   * 
   * @param {string} deliveryType - Optional: 'express', 'standard', 'weekend'. Defaults to earliest (express)
   * @returns {string} Date string in YYYY-MM-DD format
   */
  calculateFirstAvailableDeliveryDate(deliveryType = 'express') {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Pick/pack day should be next working day (not weekend)
    let pickPackDate = new Date(today);
    pickPackDate.setDate(today.getDate() + 1);
    
    // If tomorrow is weekend, move to next Monday
    while (!this.isWorkingDay(pickPackDate)) {
      pickPackDate.setDate(pickPackDate.getDate() + 1);
    }
    
    const lowered = (deliveryType || 'express').toLowerCase();
    
    // Express delivery: 1 day + 1 day pick/pack = 2 days total
    if (lowered === 'express') {
      const expressDate = new Date(pickPackDate);
      expressDate.setDate(pickPackDate.getDate() + 1);
      
      // If the next day is weekend, move to next Monday
      while (!this.isWorkingDay(expressDate)) {
        expressDate.setDate(expressDate.getDate() + 1);
      }
      
      const year = expressDate.getFullYear();
      const month = String(expressDate.getMonth() + 1).padStart(2, '0');
      const day = String(expressDate.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    // Standard delivery: 3 working days + 1 day pick/pack
    if (lowered === 'standard' || !lowered) {
      let currentDate = new Date(pickPackDate);
      let workingDaysAdded = 0;
      
      while (workingDaysAdded < 3) {
        currentDate.setDate(currentDate.getDate() + 1);
        if (this.isWorkingDay(currentDate)) {
          workingDaysAdded++;
        }
      }
      
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    // Weekend/Saturday: Allow any Saturday/Sunday from tomorrow onwards
    // But for min date, use express date so they can select weekends (when Express exists)
    if (lowered === 'weekend' || lowered === 'saturday') {
      return this.calculateFirstAvailableDeliveryDate(this.expressAvailable ? 'express' : 'standard');
    }
    
    // Default to earliest allowed window
    return this.calculateFirstAvailableDeliveryDate(this.expressAvailable ? 'express' : 'standard');
  }

  /**
   * Refresh cart data and attempt to update common cart UI without page reload
   */
  async refreshCartUI() {
    /** @type {{cart:any, sections: Record<string, string>}} */
    const state = { cart: null, sections: {} };
    try {
      const cartRes = await fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } });
      if (cartRes.ok) {
        state.cart = await cartRes.json();
        // Expose for themes that watch window.cart
        // @ts-ignore
        window.cart = state.cart;
      }

      // Attempt section refresh for common cart UIs (best-effort)
      const sectionsRes = await fetch('/?sections=cart-drawer,cart-icon-bubble,cart-notification,mini-cart,cart-items', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } });
      if (sectionsRes.ok) {
        const sectionsJson = await sectionsRes.json().catch(() => ({}));
        const sectionKeys = Object.keys(sectionsJson || {});
        state.sections = sectionsJson || {};

        // cart-drawer (Dawn style)
        const cartDrawer = document.querySelector('cart-drawer');
        const drawerHasRender = cartDrawer && typeof (/** @type {any} */ (cartDrawer)).renderContents === 'function';
        if (cartDrawer && drawerHasRender) {
          (/** @type {any} */ (cartDrawer)).renderContents({
            sections: sectionsJson,
            sections_to_render: sectionKeys
          });
        }

        // cart notification (if present)
        const cartNotification = document.querySelector('cart-notification');
        const notificationHasRender = cartNotification && typeof (/** @type {any} */ (cartNotification)).renderContents === 'function';
        if (cartNotification && notificationHasRender && sectionsJson['cart-notification']) {
          (/** @type {any} */ (cartNotification)).renderContents(sectionsJson['cart-notification']);
        }

        // cart icon bubble
        const bubble = document.querySelector('[data-cart-icon-bubble]');
        if (bubble && sectionsJson['cart-icon-bubble']) {
          bubble.innerHTML = sectionsJson['cart-icon-bubble'];
        }

        // mini-cart content (if theme uses a mini-cart section)
        const miniCart = document.querySelector('[data-mini-cart]');
        if (miniCart && sectionsJson['mini-cart']) {
          miniCart.innerHTML = sectionsJson['mini-cart'];
        }

        // Try to open cart drawer/notification if available
        const drawerToggle = document.querySelector('[data-cart-trigger], [data-cart-open], [data-cart-drawer-toggle]');
        if (drawerToggle && typeof drawerToggle.dispatchEvent === 'function') {
          drawerToggle.dispatchEvent(new Event('click', { bubbles: true }));
        }
        document.dispatchEvent(new CustomEvent('cart-drawer:open'));
        document.dispatchEvent(new CustomEvent('cart-notification:show'));
      }
      this.updateCartStateUI(state.cart);
      
      // Check for Zone 4 items and prevent checkout if found
      this.checkCartForZone4(state.cart);
    } catch (e) {
      console.warn('Cart refresh failed', e);
    }
    return state;
  }

  /**
   * Check cart for Zone 4 items and prevent checkout if found
   * @param {any} cartJson - Cart data from Shopify
   */
  checkCartForZone4(cartJson) {
    if (!cartJson || !cartJson.items) return;
    
    // Check cart attributes for Zone 4
    const deliveryZone = cartJson.attributes?._delivery_zone || cartJson.attributes?.delivery_zone || '';
    if (deliveryZone === 'Zone 4') {
      this.preventCheckoutForZone4();
      return;
    }
    
    // Also check line item properties for Zone 4
    const hasZone4 = cartJson.items.some((/** @type {any} */ item) => {
      if (!item.properties) return false;
      const zone = item.properties._delivery_zone || item.properties['Delivery Zone'] || '';
      return zone === 'Zone 4';
    });
    
    if (hasZone4) {
      this.preventCheckoutForZone4();
    }
  }

  /**
   * Update cart icon state and open drawer (theme parity)
   * @param {any} cartJson
   */
  updateCartStateUI(cartJson) {
    const itemCount = cartJson?.item_count || 0;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (cartIcon) {
      if (itemCount > 0) {
        cartIcon.classList.add('header-actions__cart-icon--has-cart');
      } else {
        cartIcon.classList.remove('header-actions__cart-icon--has-cart');
      }
    }

    // Ensure drawer is flagged open when items are added
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      if (itemCount > 0) {
        cartDrawer.setAttribute('open', '');
      }
    }
  }

  /**
   * Enable/disable Add to Cart
   */
  /** @param {boolean} enabled */
  setAddToCartEnabled(enabled) {
    const addToCartButton = this.refs.addToCartButton;
    if (addToCartButton && addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enabled;
      addToCartButton.classList.toggle('is-disabled', !enabled);
    }
  }

  /**
   * Loading state for Add to Cart
   */
  /** @param {boolean} isLoading */
  setAddToCartLoading(isLoading) {
    const addToCartButton = this.refs.addToCartButton;
    if (addToCartButton && addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = isLoading;
      addToCartButton.classList.toggle('is-loading', isLoading);
      addToCartButton.textContent = isLoading ? 'Adding…' : 'Add to cart – delivery included';
    }
  }

  /**
   * Delivery result message
   */
  /** @param {string} message @param {'info'|'error'|'success'} type */
  setDeliveryResult(message, type = 'info') {
    const deliveryResult = this.refs.deliveryResult;
    if (deliveryResult && deliveryResult instanceof HTMLElement) {
      deliveryResult.textContent = message;
      deliveryResult.classList.remove('is-error', 'is-success');
      if (type === 'error') {
        deliveryResult.classList.add('is-error');
        this.hidePricingBreakdown();
      }
      if (type === 'success') deliveryResult.classList.add('is-success');
    }
  }

  /**
   * Error/info message for CTA actions
   */
  /** @param {string} message @param {'error'|'success'} type */
  setActionMessage(message, type = 'error') {
    const actionError = this.refs.actionError;
    if (actionError && actionError instanceof HTMLElement) {
      actionError.textContent = message;
      actionError.classList.remove('is-success', 'is-error');
      actionError.classList.add(type === 'success' ? 'is-success' : 'is-error');
      actionError.style.display = message ? 'block' : 'none';
    }
  }

  clearActionMessage() {
    const actionError = this.refs.actionError;
    if (actionError && actionError instanceof HTMLElement) {
      actionError.textContent = '';
      actionError.style.display = 'none';
      actionError.classList.remove('is-success', 'is-error');
    }
  }

  /**
   * Update postcode field state based on whether date is selected
   */
  updatePostcodeFieldState() {
    const deliveryDateInput = this.refs.deliveryDateInput;
    const deliveryPostcodeInput = this.refs.deliveryPostcodeInput;
    const deliveryTypeSelect = this.refs.deliveryTypeSelect;
    
    const hasDate = deliveryDateInput && 
                    deliveryDateInput instanceof HTMLInputElement && 
                    deliveryDateInput.value !== '';
    
    if (deliveryPostcodeInput && deliveryPostcodeInput instanceof HTMLInputElement) {
      // Disable postcode if no date selected
      deliveryPostcodeInput.disabled = !hasDate;
      deliveryPostcodeInput.style.opacity = hasDate ? '1' : '0.6';
      deliveryPostcodeInput.style.cursor = hasDate ? 'text' : 'not-allowed';
      
      // Clear postcode if date is removed
      if (!hasDate && deliveryPostcodeInput.value) {
        deliveryPostcodeInput.value = '';
        this.setDeliveryResult('Please select a delivery date first.', 'error');
      }
    }
    
    if (deliveryTypeSelect && deliveryTypeSelect instanceof HTMLSelectElement) {
      // Also disable delivery type select if no date
      deliveryTypeSelect.disabled = !hasDate;
      deliveryTypeSelect.style.opacity = hasDate ? '1' : '0.6';
      deliveryTypeSelect.style.cursor = hasDate ? 'pointer' : 'not-allowed';
    }
  }

  /**
   * Update delivery type select options based on selected date
   * - If weekend date: show only Saturday option
   * - If non-weekend date: show Standard and Express options (hide Saturday)
   * @param {string} selectedDate
   */
  updateDeliveryTypeOptions(selectedDate) {
    const deliveryTypeSelect = this.refs.deliveryTypeSelect;
    if (!deliveryTypeSelect || !(deliveryTypeSelect instanceof HTMLSelectElement)) return;

    const isWeekend = selectedDate ? this.isWeekendDate(selectedDate) : false;
    const currentValue = deliveryTypeSelect.value;

    // Get all options
    const standardOption = Array.from(deliveryTypeSelect.options).find(opt => opt.value === 'standard');
    const expressOption = Array.from(deliveryTypeSelect.options).find(opt => opt.value === 'express');
    const weekendOption = Array.from(deliveryTypeSelect.options).find(opt => opt.value === 'weekend' || opt.value === 'saturday');

    if (isWeekend) {
      // Weekend date: show only Saturday, hide Standard and Express
      if (standardOption) standardOption.style.display = 'none';
      if (expressOption) expressOption.style.display = 'none';
      if (weekendOption) {
        weekendOption.style.display = '';
        // If currently selected option is hidden, switch to weekend
        if (currentValue === 'standard' || currentValue === 'express') {
          deliveryTypeSelect.value = weekendOption.value;
          this.deliveryState.type = weekendOption.value;
        }
      }
    } else {
      // Non-weekend date: show Standard and Express (if enabled), hide Saturday
      if (standardOption) standardOption.style.display = '';
      if (expressOption) {
        expressOption.style.display = this.expressAvailable ? '' : 'none';
        if (!this.expressAvailable && currentValue === 'express') {
          deliveryTypeSelect.value = 'standard';
          this.deliveryState.type = 'standard';
        }
      }
      if (weekendOption) {
        weekendOption.style.display = 'none';
        // If currently selected option is hidden (weekend), switch to standard
        if (currentValue === 'weekend' || currentValue === 'saturday' || currentValue === weekendOption.value) {
          deliveryTypeSelect.value = 'standard';
          this.deliveryState.type = 'standard';
        }
      }
    }
  }

  /**
   * Earliest calendar date allowed in the date picker
   * @returns {string} YYYY-MM-DD
   */
  getEffectiveMinimumDeliveryDateString() {
    const minDates = this.calculateMinimumDeliveryDates();
    return this.expressAvailable ? minDates.express : minDates.standard;
  }

  /**
   * Calculate minimum delivery dates for standard and express delivery
   * Standard: Today + 1 day pick/pack + 2 working days delivery = 3 working days minimum
   * Express: Today + 1 day pick/pack + 1 working day delivery = 2 working days minimum
   * @returns {{standard: string, express: string}} Date strings in YYYY-MM-DD format
   */
  calculateMinimumDeliveryDates() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Pick/pack day should be next working day (not weekend)
    let pickPackDate = new Date(today);
    pickPackDate.setDate(today.getDate() + 1);
    
    // If tomorrow is weekend, move to next Monday
    while (!this.isWorkingDay(pickPackDate)) {
      pickPackDate.setDate(pickPackDate.getDate() + 1);
    }
    
    // Express: 1 working day delivery after pick/pack
    const expressDate = this.addWorkingDays(pickPackDate, 1);
    
    // Standard: 2 working days delivery after pick/pack
    const standardDate = this.addWorkingDays(pickPackDate, 2);
    
    const formatDate = (/** @type {Date} */ date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      standard: formatDate(standardDate),
      express: formatDate(expressDate)
    };
  }

  /**
   * Show UX feedback message for auto-selected delivery type
   * @param {string} message
   * @param {string} type - 'info', 'success', 'warning'
   */
  showDeliveryTypeFeedback(message, type = 'info') {
    // Find the date input field and its parent container
    const deliveryDateInput = this.refs.deliveryDateInput;
    if (!deliveryDateInput || !(deliveryDateInput instanceof HTMLElement)) {
      console.warn('Calculator: Could not find deliveryDateInput for feedback');
      return;
    }
    
    // Find the parent field container (product-calculator__delivery-field)
    const dateFieldContainer = deliveryDateInput.closest('.product-calculator__delivery-field');
    if (!dateFieldContainer || !(dateFieldContainer instanceof HTMLElement)) {
      console.warn('Calculator: Could not find date field container for feedback');
      return;
    }
    
    // Create or update feedback element - insert after the hint div
    let feedbackEl = dateFieldContainer.querySelector('.delivery-type-feedback');
    if (!feedbackEl) {
      feedbackEl = document.createElement('div');
      feedbackEl.className = 'delivery-type-feedback';
      if (feedbackEl instanceof HTMLElement) {
        feedbackEl.style.cssText = 'margin-top: 0.5rem; font-size: 0.875rem; padding: 0.5rem; border-radius: 4px;';
      }
      
      // Insert after the hint div
      const hintDiv = dateFieldContainer.querySelector('.product-calculator__delivery-hint');
      if (hintDiv) {
        // Insert after hint div
        if (hintDiv.nextSibling) {
          dateFieldContainer.insertBefore(feedbackEl, hintDiv.nextSibling);
        } else {
          dateFieldContainer.appendChild(feedbackEl);
        }
      } else {
        // If no hint, insert after date input
        if (deliveryDateInput.nextSibling) {
          dateFieldContainer.insertBefore(feedbackEl, deliveryDateInput.nextSibling);
        } else {
          dateFieldContainer.appendChild(feedbackEl);
        }
      }
    }
    
    // Set styling based on type
    /** @type {Record<string, {backgroundColor: string, color: string, border: string}>} */
    const styles = {
      info: { backgroundColor: 'rgba(59, 130, 246, 0.1)', color: 'rgb(59, 130, 246)', border: '1px solid rgba(59, 130, 246, 0.2)' },
      success: { backgroundColor: 'rgba(34, 197, 94, 0.1)', color: 'rgb(34, 197, 94)', border: '1px solid rgba(34, 197, 94, 0.2)' },
      warning: { backgroundColor: 'rgba(251, 191, 36, 0.1)', color: 'rgb(251, 191, 36)', border: '1px solid rgba(251, 191, 36, 0.2)' }
    };
    
    const style = styles[type] || styles.info;
    if (feedbackEl instanceof HTMLElement) {
      Object.assign(feedbackEl.style, style);
      feedbackEl.textContent = message;
    }
    
    // Message stays visible - no auto-hide
    // Only hide when postcode is entered (handled in hideDeliveryTypeFeedback)
  }

  /**
   * Hide delivery type feedback message
   */
  hideDeliveryTypeFeedback() {
    // Clear timeout if exists
    if (this.deliveryTypeFeedbackTimeout) {
      clearTimeout(this.deliveryTypeFeedbackTimeout);
      this.deliveryTypeFeedbackTimeout = null;
    }
    
    // Find and remove feedback element from date field container
    const deliveryDateInput = this.refs.deliveryDateInput;
    if (deliveryDateInput && deliveryDateInput instanceof HTMLElement) {
      const dateFieldContainer = deliveryDateInput.closest('.product-calculator__delivery-field');
      if (dateFieldContainer) {
        const feedbackEl = dateFieldContainer.querySelector('.delivery-type-feedback');
        if (feedbackEl) {
          feedbackEl.remove();
        }
      }
    }
    
    // Also check deliveryResult as fallback
    const deliveryResult = this.refs.deliveryResult;
    if (deliveryResult && deliveryResult instanceof HTMLElement) {
      const feedbackEl = deliveryResult.querySelector('.delivery-type-feedback');
      if (feedbackEl) {
        feedbackEl.remove();
      }
    }
  }

  /**
   * Sync delivery type select based on selected date with auto-logic
   * - If date < standardMinDate: Auto-select Express
   * - If date >= standardMinDate: Use Standard
   * - If date is Saturday/Sunday: Auto-select Saturday
   * @param {string} selectedDate
   */
  syncTypeWithDate(selectedDate) {
    const deliveryTypeSelect = this.refs.deliveryTypeSelect;
    if (!deliveryTypeSelect || !(deliveryTypeSelect instanceof HTMLSelectElement)) return;

    // Update available options based on date
    this.updateDeliveryTypeOptions(selectedDate);

    if (!selectedDate) {
      // If no date selected, keep current selection or default to standard
      if (!deliveryTypeSelect.value) {
        deliveryTypeSelect.value = 'standard';
        this.deliveryState.type = 'standard';
      }
      this.hideDeliveryTypeFeedback();
      return;
    }

    const isWeekend = this.isWeekendDate(selectedDate);
    const isSunday = this.isWeekendDate(selectedDate) && this.getDayOfWeek(selectedDate) === 0;

    // Disable Sundays - they should not be selectable
    if (isSunday) {
      this.setDeliveryResult('Sunday delivery is not available. Please select a different date.', 'error');
      const deliveryDateInput = this.refs.deliveryDateInput;
      if (deliveryDateInput && deliveryDateInput instanceof HTMLInputElement) {
        deliveryDateInput.value = '';
      }
      this.hideDeliveryTypeFeedback();
      return;
    }

    // Calculate minimum dates
    const minDates = this.calculateMinimumDeliveryDates();
    const selectedDateObj = this.parseDate(selectedDate);
    const standardMinDateObj = this.parseDate(minDates.standard);

    // Always auto-select based on date (no manual selection allowed)
    let autoSelectedType = null;
    let feedbackMessage = '';

    if (isWeekend) {
      // Weekend date: Auto-select Saturday delivery
      const weekendOption = Array.from(deliveryTypeSelect.options).find(opt => opt.value === 'weekend' || opt.value === 'saturday');
      if (weekendOption) {
        autoSelectedType = weekendOption.value;
        feedbackMessage = 'Based on your selected delivery date Saturday delivery is applied.';
      }
    } else if (selectedDateObj && standardMinDateObj && selectedDateObj < standardMinDateObj) {
      // Date is earlier than standard minimum: Auto-select Express when offered
      if (this.expressAvailable) {
        const expressOption = Array.from(deliveryTypeSelect.options).find(opt => opt.value === 'express');
        if (expressOption) {
          autoSelectedType = 'express';
          feedbackMessage = 'Based on your selected delivery date Express delivery is applied.';
        }
      } else {
        const standardOption = Array.from(deliveryTypeSelect.options).find(opt => opt.value === 'standard');
        if (standardOption) {
          autoSelectedType = 'standard';
          feedbackMessage = 'Based on your selected delivery date Standard delivery is applied.';
        }
      }
    } else {
      // Date is >= standard minimum: Auto-select Standard
      const standardOption = Array.from(deliveryTypeSelect.options).find(opt => opt.value === 'standard');
      if (standardOption) {
        autoSelectedType = 'standard';
        feedbackMessage = 'Based on your selected delivery date Standard delivery is applied.';
      }
    }

    // Always apply auto-selection
    if (autoSelectedType) {
      const typeChanged = deliveryTypeSelect.value !== autoSelectedType;
      
      if (typeChanged) {
        deliveryTypeSelect.value = autoSelectedType;
        this.deliveryState.type = autoSelectedType;
        
        // Trigger change event to update delivery calculation if zone is already set
        if (this.deliveryState.zone && this.deliveryState.postcode) {
          this.handleDeliveryCheck(true);
        }
      }
      
      // Always show feedback message when date is selected (even if type didn't change)
      if (feedbackMessage) {
        this.showDeliveryTypeFeedback(feedbackMessage, 'info');
      }
    }
  }

  /**
   * Get day of week (0 = Sunday, 6 = Saturday)
   * @param {string} dateStr
   * @returns {number}
   */
  getDayOfWeek(dateStr) {
    if (!dateStr) return -1;
    const parts = dateStr.split('-').map(Number);
    if (parts.length !== 3 || !parts[0] || !parts[1] || !parts[2]) return -1;
    const [year, month, day] = parts;
    const date = new Date(year, month - 1, day);
    return date.getDay();
  }

  /**
   * Parse date string to Date object
   * @param {string} dateStr
   * @returns {Date|null}
   */
  parseDate(dateStr) {
    if (!dateStr) return null;
    const parts = dateStr.split('-').map(Number);
    if (parts.length !== 3 || !parts[0] || !parts[1] || !parts[2]) return null;
    const [year, month, day] = parts;
    return new Date(year, month - 1, day);
  }

  /**
   * Validate delivery date (prevent Sundays)
   * @param {string} dateStr
   * @returns {boolean}
   */
  /**
   * Parse blocked delivery holidays from store metafield
   * Format: "2026-01-01 | New Year's Day\n2026-04-18 | Good Friday"
   */
  parseBlockedHolidays() {
    this.blockedHolidays.clear();
    
    const holidaysData = this.dataset.deliveryHolidays || '';
    if (!holidaysData || holidaysData.trim() === '') {
      return;
    }
    
    // Decode HTML entities if present
    let decodedData = holidaysData;
    if (decodedData.includes('&quot;')) {
      decodedData = decodedData.replace(/&quot;/g, '"');
    }
    if (decodedData.includes('&#39;')) {
      decodedData = decodedData.replace(/&#39;/g, "'");
    }
    if (decodedData.includes('&amp;')) {
      decodedData = decodedData.replace(/&amp;/g, '&');
    }
    
    // Split by newlines and parse each line
    const lines = decodedData.split(/\r?\n/);
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      
      // Parse format: "YYYY-MM-DD | Holiday Name"
      const parts = trimmed.split('|').map(p => p.trim());
      if (parts.length >= 2) {
        const dateStr = parts[0] || '';
        const holidayName = parts.slice(1).join('|').trim(); // Rejoin in case holiday name contains |
        
        // Validate date format (YYYY-MM-DD)
        if (dateStr && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          this.blockedHolidays.set(dateStr, holidayName || 'Holiday');
        }
      }
    }
  }

  /**
   * Check if a date is blocked (holiday)
   * @param {string} dateStr - Date in YYYY-MM-DD format
   * @returns {{blocked: boolean, holidayName?: string}}
   */
  isDateBlocked(dateStr) {
    if (!dateStr) return { blocked: false };
    
    const holidayName = this.blockedHolidays.get(dateStr);
    if (holidayName) {
      return { blocked: true, holidayName };
    }
    
    return { blocked: false };
  }

  /**
   * Validate delivery date (prevent Sundays and blocked holidays)
   * @param {string} dateStr
   * @returns {boolean}
   */
  validateDeliveryDate(dateStr) {
    if (!dateStr) return true;
    
    // Enforce minimum date (iOS Safari often ignores input min attribute)
    const minDates = this.calculateMinimumDeliveryDates();
    const selectedObj = this.parseDate(dateStr);
    const minBoundaryStr = this.expressAvailable ? minDates.express : minDates.standard;
    const minObj = this.parseDate(minBoundaryStr);
    if (selectedObj && minObj && selectedObj < minObj) {
      const deliveryDateInput = this.refs.deliveryDateInput;
      if (deliveryDateInput && deliveryDateInput instanceof HTMLInputElement) {
        deliveryDateInput.value = '';
      }
      this.deliveryState.date = '';
      this.setAddToCartEnabled(false);
      const minFormatted = minObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      const errorMessage = `The selected date is too soon. The earliest delivery date is ${minFormatted}.`;
      this.setDeliveryResult(errorMessage, 'error');
      this.showDeliveryDateError(errorMessage);
      return false;
    }
    
    // Check if date is a blocked holiday
    const holidayCheck = this.isDateBlocked(dateStr);
    if (holidayCheck.blocked) {
      const deliveryDateInput = this.refs.deliveryDateInput;
      if (deliveryDateInput && deliveryDateInput instanceof HTMLInputElement) {
        deliveryDateInput.value = '';
      }
      this.deliveryState.date = '';
      this.setAddToCartEnabled(false);
      const errorMessage = `Delivery is not available on ${holidayCheck.holidayName}. Please select another date.`;
      this.setDeliveryResult(errorMessage, 'error');
      this.showDeliveryDateError(errorMessage);
      return false;
    }
    
    const dayOfWeek = this.getDayOfWeek(dateStr);
    if (dayOfWeek === 0) { // Sunday
      const deliveryDateInput = this.refs.deliveryDateInput;
      if (deliveryDateInput && deliveryDateInput instanceof HTMLInputElement) {
        deliveryDateInput.value = '';
      }
      this.deliveryState.date = '';
      this.setAddToCartEnabled(false);
      this.setDeliveryResult('Sunday delivery is not available. Please select a different date.', 'error');
      this.showDeliveryDateError('Sunday delivery is not available. Please select a different date.');
      return false;
    }
    
    // Clear any previous error if date is valid
    this.clearDeliveryDateError();
    return true;
  }

  /**
   * Show error message near the date picker
   * @param {string} message
   */
  showDeliveryDateError(message) {
    const deliveryDateInput = this.refs.deliveryDateInput;
    if (!deliveryDateInput || !(deliveryDateInput instanceof HTMLInputElement)) return;
    
    // Remove existing error if present
    this.clearDeliveryDateError();
    
    // Create error element
    const errorEl = document.createElement('div');
    errorEl.className = 'product-calculator__delivery-date-error';
    errorEl.setAttribute('role', 'alert');
    errorEl.style.cssText = 'color: #d32f2f; font-size: 0.875rem; margin-top: 0.5rem; padding: 0.5rem; background-color: #ffebee; border: 1px solid #d32f2f; border-radius: 4px;';
    errorEl.textContent = message;
    
    // Insert after the date input's parent container or the input itself
    const dateField = deliveryDateInput.closest('.product-calculator__delivery-field');
    if (dateField) {
      dateField.appendChild(errorEl);
    } else {
      deliveryDateInput.parentElement?.insertBefore(errorEl, deliveryDateInput.nextSibling);
    }
    
    // Add error class to input
    deliveryDateInput.classList.add('has-error');
    deliveryDateInput.setAttribute('aria-invalid', 'true');
    deliveryDateInput.setAttribute('aria-describedby', errorEl.id || 'delivery-date-error');
    if (!errorEl.id) {
      errorEl.id = `delivery-date-error-${Date.now()}`;
      deliveryDateInput.setAttribute('aria-describedby', errorEl.id);
    }
  }

  /**
   * Clear error message from date picker
   */
  clearDeliveryDateError() {
    const deliveryDateInput = this.refs.deliveryDateInput;
    if (deliveryDateInput && deliveryDateInput instanceof HTMLInputElement) {
      deliveryDateInput.classList.remove('has-error');
      deliveryDateInput.removeAttribute('aria-invalid');
      deliveryDateInput.removeAttribute('aria-describedby');
    }
    
    const errorEl = this.querySelector('.product-calculator__delivery-date-error');
    if (errorEl) {
      errorEl.remove();
    }
  }

  /**
   * Prevent checkout for Zone 4 by disabling checkout buttons and showing message
   */
  preventCheckoutForZone4() {
    // Disable checkout buttons in cart drawer and cart page
    const checkoutButtons = document.querySelectorAll(
      'a[href*="/checkout"], button[data-checkout], .cart__checkout, [data-cart-checkout], cart-drawer a[href*="/checkout"], .cart-summary__checkout, [data-cart-checkout-button]'
    );
    
    checkoutButtons.forEach((/** @type {HTMLElement} */ button) => {
      if (button instanceof HTMLAnchorElement) {
        button.style.pointerEvents = 'none';
        button.style.opacity = '0.5';
        button.style.cursor = 'not-allowed';
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('data-zone4-blocked', 'true');
        // Prevent navigation
        button.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }, true);
      } else if (button instanceof HTMLButtonElement) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('data-zone4-blocked', 'true');
      }
    });
    
    // Show message in cart if Zone 4 items are present
    let messageElement = document.querySelector('.cart__zone4-message, [data-zone4-message]');
    if (!messageElement || !(messageElement instanceof HTMLElement)) {
      // Create message element if it doesn't exist
      const newMessageElement = document.createElement('div');
      newMessageElement.className = 'cart__zone4-message';
      newMessageElement.setAttribute('data-zone4-message', 'true');
      newMessageElement.style.cssText = 'padding: 1rem; margin: 1rem 0; background-color: #ffebee; border: 1px solid #d32f2f; border-radius: 4px; color: #d32f2f; font-weight: 500;';
      messageElement = newMessageElement;
      
      // Try to insert before checkout button or at end of cart summary
      const cartSummary = document.querySelector('.cart-drawer__summary, .cart__summary, [data-cart-summary]');
      const checkoutBtn = document.querySelector('a[href*="/checkout"], button[data-checkout], .cart__checkout');
      if (checkoutBtn && checkoutBtn.parentElement) {
        checkoutBtn.parentElement.insertBefore(messageElement, checkoutBtn);
      } else if (cartSummary && cartSummary instanceof HTMLElement) {
        cartSummary.appendChild(messageElement);
      } else {
        document.body.appendChild(messageElement);
      }
    }
    
    if (messageElement instanceof HTMLElement) {
      messageElement.textContent = 'Delivery not available to your area. Please contact us for shipping options.';
      messageElement.style.display = 'block';
    }
    
    // Dispatch event for other components to handle
    document.dispatchEvent(new CustomEvent('delivery:zone4-blocked', {
      bubbles: true,
      detail: { zone: 'Zone 4', message: 'Delivery not available. Please contact us.' }
    }));
  }

  /**
   * Update delivery price and total estimate displays
   * @param {number} deliveryCostPence
   * @param {number} totalPence
   */
  updateDeliveryPricingDisplay(deliveryCostPence, totalPence) {
    const deliveryPriceDisplay = this.refs.deliveryPriceDisplay;
    const orderTotalEstimateDisplay = this.refs.orderTotalEstimateDisplay;
    if (deliveryPriceDisplay && deliveryPriceDisplay instanceof HTMLElement) {
      deliveryPriceDisplay.textContent = deliveryCostPence > 0 ? this.formatPrice(deliveryCostPence) : '—';
    }
    if (orderTotalEstimateDisplay && orderTotalEstimateDisplay instanceof HTMLElement) {
      orderTotalEstimateDisplay.textContent = totalPence > 0 ? this.formatPrice(totalPence) : '—';
    }
  }
}

customElements.define('product-calculator-component', ProductCalculator);
